import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import downloadIcon from '../assets/Icon/download_icon.svg';
import { apiService } from '../services/apiService';
import * as XLSX from 'xlsx';
import notificationService from '../CommanService/notificationService';
import './BatchPricingOverride.css'; // Import the CSS file for styling
import Multiselect from 'multiselect-react-dropdown';
import { hasPermission, PERMISSION_CODES, filterDataByUserAccess } from '../utils/permissionUtils';
import searchIcon from '../assets/Icon/search_icon.svg';
import dropdownArrow from '../assets/Icon/dropdown_arrow.svg';
import { format } from 'date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-dist';

const Plot = createPlotlyComponent(Plotly);

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const NewCom = () => {
  const { user } = useSelector(state => state.auth);
 // Check if user has Super Admin role or Analyst role
 const isSuperAdmin = hasPermission(user, PERMISSION_CODES.EDIT_ADJUSTED_PRICE);

  const [finalisedData, setFinalisedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [originalData, setOriginalData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [graphData, setGraphData] = useState({});
  const [originalGraphData, setOriginalGraphData] = useState({});
  const [activeTab, setActiveTab] = useState('data'); // 'data' | 'graph'
  const [graphLoading, setGraphLoading] = useState(false);

  const [regions, setRegions] = useState([]);
  const [interchangeNumbers, setInterchangeNumbers] = useState([]);
  const [partTypes, setPartTypes] = useState([]);
  
  // Separate dropdown options for Review Graph tab (single-select, no "All" option)
  const [regionsGraph, setRegionsGraph] = useState([]);
  const [interchangeNumbersGraph, setInterchangeNumbersGraph] = useState([]);

  // Selected values state - single-select (first available part type as default)
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedPartType, setSelectedPartType] = useState('');
  const [selectedInterchangeNumber, setSelectedInterchangeNumber] = useState('');

  // Multi-select states for Review Data tab
  const [selectedRegionsData, setSelectedRegionsData] = useState(['All']);
  const [selectedInterchangeNumbersData, setSelectedInterchangeNumbersData] = useState(['All']);

  // Searchable dropdown UI state (modeled after PriceComparativePage IC dropdown)
  const [partTypeSearch, setPartTypeSearch] = useState('');
  const [showPartTypeDropdown, setShowPartTypeDropdown] = useState(false);
  const [regionSearch, setRegionSearch] = useState('');
  const [showRegionDropdown, setShowRegionDropdown] = useState(false);
  const [icSearch, setIcSearch] = useState('');
  const [showIcDropdown, setShowIcDropdown] = useState(false);



  const [comparisonOperator, setComparisonOperator] = useState('greater');
  const [comparisonValue, setComparisonValue] = useState('');
  const [isEditModeEnabled, setIsEditModeEnabled] = useState(false);
  
  // Sorting states
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Finalized By filter states
  const [finalizedByNames, setFinalizedByNames] = useState([]);
  const [selectedFinalizedBy, setSelectedFinalizedBy] = useState('');
  const [finalizedBySearch, setFinalizedBySearch] = useState('');
  const [showFinalizedByDropdown, setShowFinalizedByDropdown] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState({
    partTypes: [],
    regions: [],
    interchangeNumbers: [],
    finalizedBy: [],
    comparisonOperator: '',
    comparisonValue: ''
  });

  // Separate filters for Review Data tab (multi-select)
  const [appliedFiltersData, setAppliedFiltersData] = useState({
    partTypes: [],
    regions: ['All'],
    interchangeNumbers: ['All'],
    finalizedBy: [],
    comparisonOperator: '',
    comparisonValue: ''
  });





  // Shared color palette and region color helper used by chart and legend
  const regionColorPalette = useMemo(() => (
    ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf']
  ), []);
  const getRegionColor = (regionName) => {
    const baseList = (appliedFilters.regions && appliedFilters.regions.length > 0) ? appliedFilters.regions : regions;
    const index = baseList.indexOf(regionName);
    const safeIndex = index >= 0 ? index : 0;
    return regionColorPalette[safeIndex % regionColorPalette.length];
  };
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Safe date-time formatter to avoid RangeError on invalid dates
  const formatDateTimeSafe = (value) => {
    try {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return format(d, 'yyyy-MM-dd HH:mm:ss');
    } catch {
      return '';
    }
  };

  // Exponential regression helpers for sales data
  const calculateExponentialRegression = (xValues, yValues) => {
    if (!Array.isArray(xValues) || !Array.isArray(yValues)) return null;
    const n = Math.min(xValues.length, yValues.length);
    if (n < 2) return null;

    // Filter for valid positive y (required for log) and finite x
    const valid = [];
    for (let i = 0; i < n; i++) {
      const x = Number(xValues[i]);
      const y = Number(yValues[i]);
      if (Number.isFinite(x) && Number.isFinite(y) && y > 0) {
        valid.push([x, y]);
      }
    }
    if (valid.length < 2) return null;

    let sumX = 0;
    let sumLogY = 0;
    let sumXLogY = 0;
    let sumX2 = 0;
    const m = valid.length;

    for (let i = 0; i < m; i++) {
      const x = valid[i][0];
      const logY = Math.log(valid[i][1]);
      sumX += x;
      sumLogY += logY;
      sumXLogY += x * logY;
      sumX2 += x * x;
    }

    const denominator = (m * sumX2 - sumX * sumX);
    if (denominator === 0) return null;
    const b = (m * sumXLogY - sumX * sumLogY) / denominator;
    const a = Math.exp((sumLogY - b * sumX) / m);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return { a, b };
  };

  // Function to calculate quartile breaks dynamically from sales data
  const getQuartileBreaks = (salesData) => {
    if (!salesData || salesData.length < 5) {
      return null;
    }

    const mileages = salesData
      .filter(s => s.mileage > 0)
      .map(s => parseFloat(s.mileage) * 1000) // Multiply by 1000
      .sort((a, b) => a - b);

    if (mileages.length < 5) {
      return null;
    }

    const count = mileages.length;
    return [
      mileages[0], // Q0 (min)
      mileages[Math.floor(count * 0.25)], // Q1 (25th percentile)in this 
      mileages[Math.floor(count * 0.5)],  // Q2 (50th percentile - median)
      mileages[Math.floor(count * 0.75)], // Q3 (75th percentile)
      mileages[count - 1] // Q4 (max)
    ];
  };

  // Function to generate quartile-based price adjustment curve based on regression
  const generateQuartileAdjustmentCurve = () => {
    try {
      // Get quartile values from API data
      const actualGraphData = graphData && graphData.data ? graphData.data : graphData;
      const inventoryData = actualGraphData && Array.isArray(actualGraphData.inventory_pe) ? actualGraphData.inventory_pe : [];
      
      const quartileConfigs = inventoryData.length > 0 && inventoryData[0].global_config && inventoryData[0].global_config.quartileConfigs 
        ? inventoryData[0].global_config.quartileConfigs 
        : [];
      
      // Get sales data for dynamic quartile calculation
      const sales = actualGraphData && Array.isArray(actualGraphData.sales) ? actualGraphData.sales : [];
      
      // Apply same filters as chart for consistency
      let filteredSales = sales.filter(item => Number(item.mileage) > 0 && item.sold_price !== null && item.sold_price !== undefined);
      
      if (appliedFilters.partTypes.length > 0) {
        filteredSales = filteredSales.filter(item => appliedFilters.partTypes.some(p => item.part_name === p));
      }
      if (appliedFilters.regions.length > 0) {
        filteredSales = filteredSales.filter(item => appliedFilters.regions.some(r => item.region === r));
      }
      if (appliedFilters.interchangeNumbers.length > 0) {
        filteredSales = filteredSales.filter(item => appliedFilters.interchangeNumbers.some(ic => item.interchange_number === ic));
      }
      
      // Calculate dynamic quartile breaks from filtered sales data
      const quartileBreaks = getQuartileBreaks(filteredSales);
      
      // Use dynamic quartile breaks if available, otherwise fall back to hardcoded ranges
      let mileageRanges;
      if (quartileBreaks && quartileBreaks.length === 5) {
        mileageRanges = [
          { min: quartileBreaks[0], max: quartileBreaks[1], quartileValue: quartileConfigs[0]?.quartileValue || 0 },
          { min: quartileBreaks[1], max: quartileBreaks[2], quartileValue: quartileConfigs[1]?.quartileValue || 0 },
          { min: quartileBreaks[2], max: quartileBreaks[3], quartileValue: quartileConfigs[2]?.quartileValue || 0 },
          { min: quartileBreaks[3], max: quartileBreaks[4], quartileValue: quartileConfigs[3]?.quartileValue || 0 }
        ];
        
        console.log('Dynamic quartile breaks calculated:', {
          quartileBreaks: quartileBreaks.map((q, i) => ({ [`Q${i}`]: Math.round(q) })),
          quartileConfigs: quartileConfigs.map((qc, i) => ({ [`Q${i+1}`]: qc?.quartileValue })),
          mileageRanges: mileageRanges.map(r => ({ 
            range: `${Math.round(r.min)}-${Math.round(r.max)}`, 
            adjustment: r.quartileValue 
          }))
        });
      } else {
        // Fallback to hardcoded ranges
        mileageRanges = [
          { min: 20000, max: 50000, quartileValue: quartileConfigs[0]?.quartileValue || 0 },
          { min: 50000, max: 78000, quartileValue: quartileConfigs[1]?.quartileValue || 0 },
          { min: 78000, max: 90000, quartileValue: quartileConfigs[2]?.quartileValue || 0 },
          { min: 90000, max: 135000, quartileValue: quartileConfigs[3]?.quartileValue || 0 }
        ];
        console.log('Using hardcoded quartile ranges (insufficient sales data)');
      }
      
      // Get regression coefficients from sales data
      if (filteredSales.length < 2) {
        console.log('Not enough filtered sales data for regression-based quartile curve');
        return null;
      }
      
      // Calculate regression coefficients
      const x = filteredSales.map(s => (parseFloat(s.mileage) || 0) * 1000);
      const y = filteredSales.map(s => parseFloat(s.sold_price) || 0);
      const coeff = calculateExponentialRegression(x, y);
      
      if (!coeff) {
        console.log('Could not calculate regression coefficients for quartile curve');
        return null;
      }
      
      // Generate curve points based on regression with quartile adjustments
      const curvePoints = [];
      const stepSize = 1000; // 1000-mile steps
      
      // Find the overall mileage range from the data
      const allMileages = [...x, ...(inventoryData.map(item => parseFloat(item.mileage) || 0))];
      const minMileage = Math.min(...allMileages.filter(m => m > 0));
      const maxMileage = Math.max(...allMileages.filter(m => m > 0));
      
      // Generate points across the entire mileage range
      for (let mileage = minMileage; mileage <= maxMileage; mileage += stepSize) {
        // Calculate base regression price
        const regressionPrice = coeff.a * Math.exp(coeff.b * mileage);
        
        // Check if this mileage falls within any quartile range
        let adjustedPrice = regressionPrice;
        let currentQuartile = null;
        let adjustmentPercent = 0;
        
        // Find the appropriate quartile range for this mileage
        let foundRange = null;
        for (const range of mileageRanges) {
          if (mileage >= range.min && mileage <= range.max) {
            foundRange = range;
            break;
          }
        }
        
        // If no range found, use appropriate quartile based on mileage position
        if (!foundRange && mileageRanges.length > 0) {
          const firstRange = mileageRanges[0]; // Q1
          const lastRange = mileageRanges[mileageRanges.length - 1]; // Q4
          
          if (mileage < firstRange.min) {
            // Use Q1 adjustment for mileages before Q1 starts
            foundRange = firstRange;
          } else if (mileage > lastRange.max) {
            // Use Q4 adjustment for mileages beyond Q4
            foundRange = lastRange;
          }
        }
        
        if (foundRange) {
          // Apply quartile adjustment to regression price (even if quartileValue is 0)
          adjustedPrice = regressionPrice * (1 + foundRange.quartileValue / 100);
          currentQuartile = `Q${mileageRanges.indexOf(foundRange) + 1}`;
          adjustmentPercent = foundRange.quartileValue;
        }
        
        curvePoints.push({
          mileage: mileage,
          price: adjustedPrice,
          quartile: currentQuartile || 'Base',
          adjustment: adjustmentPercent,
          regressionPrice: regressionPrice
        });
      }
      
      const quartileTrace = {
        x: curvePoints.map(p => p.mileage),
        y: curvePoints.map(p => p.price),
        mode: 'lines',
        type: 'scatter',
        name: 'Price Line %',
        line: {
          color: '#ff7f0e',
          width: 3,
          dash: '10px,5px'
        },
        hovertemplate: '<b>Price Line %</b><br>' +
                      '<b>Mileage:</b> %{x:,.0f} miles<br>' +
                      '<b>Adjusted Price:</b> $%{y:,.2f}<br>' +
                      '<b>Base Regression:</b> $%{customdata[2]:,.2f}<br>' +
                      '<b>Quartile:</b> %{customdata[0]}<br>' +
                      '<b>Adjustment:</b> %{customdata[1]}%<br>' +
                      '<extra></extra>',
        customdata: curvePoints.map(p => [p.quartile, p.adjustment, p.regressionPrice])
      };
      
      console.log('Generated quartile-adjusted regression curve:', {
        regressionCoeff: coeff,
        curvePoints: curvePoints.length,
        ranges: mileageRanges.map(r => ({ 
          range: `${Math.round(r.min)}-${Math.round(r.max)}`, 
          adjustment: r.quartileValue 
        })),
        adjustedPoints: curvePoints.filter(p => p.adjustment !== 0).length,
        quartileBreakdown: curvePoints.reduce((acc, p) => {
          if (p.quartile !== 'Base') {
            acc[p.quartile] = (acc[p.quartile] || 0) + 1;
          }
          return acc;
        }, {}),
        note: 'Q1 adjustment continues before Q1 range, Q4 adjustment continues beyond Q4 range to prevent curve drops'
      });
      
      return quartileTrace;
    } catch (error) {
      console.error('Error generating quartile adjustment curve:', error);
      return null;
    }
  };

  // Unified chart data preparation function for all price types
  const prepareUnifiedChartData = () => {
    console.log('=== prepareUnifiedChartData Debug ===');
    console.log('graphData:', graphData);
    console.log('filteredData length:', filteredData ? filteredData.length : 'undefined');
    
    // Check if graphData has the expected structure
    let actualGraphData = null;
    if (graphData && graphData.data) {
      actualGraphData = graphData.data;
      console.log('Using graphData.data structure');
    } else if (graphData && (graphData.inventory_pe || graphData.sales)) {
      actualGraphData = graphData;
      console.log('Using graphData directly (no .data wrapper)');
    } else {
      console.log('No valid graph data available');
      return [];
    }
    
    const allTraces = [];
    const symbols = ['circle', 'square', 'diamond', 'x', 'triangle-up', 'star', 'hexagon', 'pentagon'];
    
    // Price types to process (only Revised Price from inventory and Sold Price from sales)
    const priceTypes = [
      { field: 'received_price', name: 'Inventory', color: '#2ca02c', symbol: 'circle' }, // green
      { field: 'sold_price', name: 'Sold', color: '#d62728', symbol: 'x' } // red
    ];
    
    priceTypes.forEach((priceType, priceIndex) => {
      let sourceData = [];
      
      // Get data based on price type
      if (priceType.field === 'sold_price' && actualGraphData && actualGraphData.sales) {
        sourceData = actualGraphData.sales;
        console.log(`Using sales data for ${priceType.field}:`, sourceData.length, 'items');
      } else if (actualGraphData && actualGraphData.inventory_pe) {
        sourceData = actualGraphData.inventory_pe;
        console.log(`Using inventory_pe data for ${priceType.field}:`, sourceData.length, 'items');
      } else {
        console.log(`No data available for ${priceType.field}`);
        return;
      }
      
      if (sourceData.length === 0) {
        console.log(`No source data available for ${priceType.field}`);
        return;
      }

      // Apply filters based on appliedFilters state
      let filteredSourceData = sourceData;

      // Filter by part type if any selected
      if (appliedFilters.partTypes.length > 0) {
        filteredSourceData = filteredSourceData.filter(item => 
          appliedFilters.partTypes.some(partType => item.part_name === partType)
        );
      }

      // Filter by region if any selected
      if (appliedFilters.regions.length > 0) {
        filteredSourceData = filteredSourceData.filter(item => 
          appliedFilters.regions.some(region => item.region === region)
        );
      }

      // Filter by IC number if any selected
      if (appliedFilters.interchangeNumbers.length > 0) {
        filteredSourceData = filteredSourceData.filter(item => 
          appliedFilters.interchangeNumbers.some(icNumber => item.interchange_number === icNumber)
        );
      }

      // Group data by region for this price type
      const regionGroups = {};
      let validPointsCount = 0;
      
      filteredSourceData.forEach((item, index) => {
        const region = item.region || 'Unknown';
        if (!regionGroups[region]) {
          regionGroups[region] = [];
        }
        
        // Only include valid data points (allow 0 prices but require positive mileage)
        if (item.mileage > 0 && item[priceType.field] !== null && item[priceType.field] !== undefined) {
          // Handle mileage differently for sales data vs inventory_pe data
          let mileageValue = parseFloat(item.mileage) || 0;
          
          // If this is sold_price data from sales array, multiply by 1000 to match inventory_pe scale
          if (priceType.field === 'sold_price' && actualGraphData && actualGraphData.sales) {
            mileageValue = mileageValue * 1000;
          }
          
          regionGroups[region].push({
            mileage: mileageValue,
            price: parseFloat(item[priceType.field]) || 0,
            region: region,
            partName: item.part_name || 'Unknown',
            icNumber: item.interchange_number || 'Unknown',
            priceType: priceType.name
          });
          validPointsCount++;
        }
      });
      
      console.log(`Data processing summary for ${priceType.field}:`, {
        totalItems: filteredSourceData.length,
        validPoints: validPointsCount,
        regions: Object.keys(regionGroups)
      });

      // Create traces for each region
      Object.keys(regionGroups).forEach((region, regionIndex) => {
        const regionData = regionGroups[region];
        
        if (regionData.length === 0) {
          return;
        }

        // Sort data by mileage
        regionData.sort((a, b) => a.mileage - b.mileage);

        // Create scatter plot for this price type and region
        // For Revised Price, include Predicted and Original prices in tooltip
        const isRevised = priceType.field === 'received_price';
        const trace = {
          x: regionData.map(d => d.mileage),
          y: regionData.map(d => d.price),
          mode: 'markers',
          type: 'scatter',
          name: `${priceType.name} (${region})`,
          marker: {
            color: priceType.color,
            size: 12,
            opacity: 0.85,
            symbol: priceType.symbol,
            line: {
              color: 'white',
              width: 2
            }
          },
          hovertemplate: isRevised
            ? (
              `<b>${priceType.name}</b><br>` +
              `<b>Region:</b> ${region}<br>` +
              `<b>Part Name:</b> %{customdata[0]}<br>` +
              `<b>IC Number:</b> %{customdata[1]}<br>` +
              `<b>Mileage:</b> %{x:,.0f} miles<br>` +
              `<b>Predicted Price:</b> $%{customdata[2]:,.2f}<br>` +
              `<b>Original Price:</b> $%{customdata[3]:,.2f}<br>` +
              `<b>Revised Price:</b> $%{y:,.2f}<br>` +
              `<extra></extra>`
            )
            : (
              `<b>${priceType.name}</b><br>` +
              `<b>Region:</b> ${region}<br>` +
              `<b>Part Name:</b> %{customdata[0]}<br>` +
              `<b>IC Number:</b> %{customdata[1]}<br>` +
              `<b>Mileage:</b> %{x:,.0f} miles<br>` +
              `<b>Price:</b> $%{y:,.2f}<br>` +
              `<extra></extra>`
            ),
          customdata: isRevised
            ? regionData.map(d => {
                // Find the exact matching item from the original source data
                const matchingItem = sourceData.find(s => 
                  s.interchange_number === d.icNumber && 
                  (s.region || 'Unknown') === d.region &&
                  s.part_name === d.partName &&
                  parseFloat(s.mileage) === d.mileage
                );
                
                return [
                  d.partName,
                  d.icNumber,
                  parseFloat(matchingItem?.predicted_price) || 0,
                  parseFloat(matchingItem?.original_price) || 0
                ];
              })
            : regionData.map(d => [d.partName, d.icNumber])
        };
        
        allTraces.push(trace);
      });
    });
    
    // Append regression curve based on sales data if available
    if (Array.isArray(regressionPoints) && regressionPoints.length > 1) {
      const regressionTrace = {
        x: regressionPoints.map(p => p.mileage),
        y: regressionPoints.map(p => p.predictedPrice),
        mode: 'lines',
        type: 'scatter',
        name: 'Exponential Fit (Sales)',
        line: {
          color: '#ff7f0e',
          width: 4
        },
        hovertemplate: '<b>Regression Prediction</b><br>' +
                      '<b>Mileage:</b> %{x:,.0f} miles<br>' +
                      '<b>Predicted Price:</b> $%{y:,.2f}<br>' +
                      '<b>Interval:</b> 2000-mile steps<br>' +
                      '<extra></extra>'
      };
      allTraces.push(regressionTrace);
    }

    // Add quartile-based price adjustment curve
    const quartileAdjustmentCurve = generateQuartileAdjustmentCurve();
    if (quartileAdjustmentCurve) {
      allTraces.push(quartileAdjustmentCurve);
    }

    console.log(`Final unified chart traces:`, {
      traceCount: allTraces.length,
      totalPoints: allTraces.reduce((sum, trace) => sum + trace.x.length, 0)
    });

    return allTraces;
  };


  // Function to prepare unified chart layout
  const prepareUnifiedChartLayout = () => {
    // Build title with applied filters
    let fullTitle = 'Price Analysis - All Price Types vs Mileage';
    
    // Add part type filter if any selected
    if (appliedFilters.partTypes.length > 0) {
      fullTitle += ` - ${appliedFilters.partTypes.join(', ')}`;
    }
    
    // Add IC number filter if any selected
    if (appliedFilters.interchangeNumbers.length > 0) {
      fullTitle += ` (IC: ${appliedFilters.interchangeNumbers.join(', ')})`;
    }
    
    // Add region filter if any selected
    if (appliedFilters.regions.length > 0) {
      fullTitle += ` [Regions: ${appliedFilters.regions.join(', ')}]`;
    }
    
    return {
      title: {
        text: fullTitle,
        font: { size: 18, color: '#333', family: 'Arial, sans-serif' }
      },
      xaxis: {
        title: { 
          text: 'Mileage (miles)', 
          font: { size: 14, family: 'Arial, sans-serif' } 
        },
        showgrid: true,
        zeroline: false,
        tickformat: ',d',
        gridcolor: 'rgba(128,128,128,0.2)',
        gridwidth: 1
      },
      yaxis: {
        title: { 
          text: 'Price ($)', 
          font: { size: 14, family: 'Arial, sans-serif' } 
        },
        showgrid: true,
        zeroline: false,
        tickformat: '$,.0f',
        gridcolor: 'rgba(128,128,128,0.2)',
        gridwidth: 1
      },
      plot_bgcolor: '#fff',
      paper_bgcolor: '#f8f9fa',
      margin: { t: 80, l: 80, r: 20, b: 80 },
      showlegend: true,
      legend: {
        x: 0.98,
        xanchor: 'right',
        y: 0.98,
        yanchor: 'top',
        bgcolor: 'rgba(255,255,255,0.95)',
        bordercolor: '#ccc',
        borderwidth: 1,
        font: { size: 12, family: 'Arial, sans-serif' },
        itemwidth: 30,
        itemsizing: 'constant',
        orientation: 'v'
      },
      hovermode: 'closest',
      hoverlabel: {
        bgcolor: 'rgba(0,0,0,0.8)',
        bordercolor: '#333',
        font: { size: 12, family: 'Arial, sans-serif', color: 'white' },
        namelength: -1,
        split: false
      }
    };
  };












  const handleDownloadExcel = () => {
    // Use filteredData instead of finalisedData to export only filtered results
    const dataToExport = filteredData;
    if (dataToExport.length === 0) {
      notificationService.showWarning('No data to export');
      return;
    }
    
    const excelData = dataToExport.map(item => {
      const originalPrice = parseFloat(item.original_price) || 0;
      const adjustedPrice = parseFloat(item.adjusted_price) || 0;
      const priceDifference = adjustedPrice - originalPrice;
      const percentageDifference = originalPrice > 0
        ? ((adjustedPrice - originalPrice) / originalPrice * 100).toFixed(2)
        : '0.00';
      
      return {
        'Part Name': item.part_name || '',
        'Region': item.region || '',
        'IC Number': item.interchange_number || '',
        'Mileage': item.mileage || 0,
        'Original Price': originalPrice,
        'Adjusted Price': adjustedPrice,
        'Price Difference': priceDifference,
        '% Difference': percentageDifference + '%',
        '$ Positive % Limit': item.dollar_positive_perc_limit || 0,
        '$ Negative % Limit': item.dollar_negative_perc_limit || 0,
        'Model Predicated Price': item.model_predicated_price || 0,
        'Date&Time': formatDateTimeSafe(item.created_on),
        'Approved By': item.approved_by_name || ''
      };
    });

    // Create workbook with multiple sheets
    const workbook = XLSX.utils.book_new();
    
    // Main data sheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Batch Pricing Override Data');
    const today = new Date();
    const dateString = today.toISOString().split('T')[0];
    const fileName = `Batch_Pricing_Override_Report_${dateString}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };


 // Fetch finalised data on component mount
  useEffect(() => {
    const fetchFinalisedData = async () => {
      try {
        setLoading(true);
        const response = await apiService.GetFinalisedData();
        if (response && response.data) {
          setFinalisedData(response.data);
          setOriginalData([...response.data]); // Store original data for comparison
          
          // Extract unique values for dropdowns
          const uniqueRegions = ['All', ...new Set(response.data.map(item => item.region).filter(Boolean))];
          const uniqueInterchanges = ['All', ...new Set(response.data.map(item => item.interchange_number).filter(Boolean))];
          const uniquePartName = [...new Set(response.data.map(item => item.part_name).filter(Boolean))];
          const uniqueFinalizedBy = [...new Set(response.data.map(item => item.approved_by_name).filter(Boolean))];
          
          setRegions(uniqueRegions);
          setInterchangeNumbers(uniqueInterchanges);
          setPartTypes(uniquePartName);
          setFinalizedByNames(uniqueFinalizedBy);
          
          // Set Graph-specific dropdown options (without "All" option)
          setRegionsGraph([...new Set(response.data.map(item => item.region).filter(Boolean))]);
          setInterchangeNumbersGraph([...new Set(response.data.map(item => item.interchange_number).filter(Boolean))]);
          
          // Set first available part type as default
          if (uniquePartName.length > 0) {
            setSelectedPartType(uniquePartName[0]);
            setPartTypeSearch(uniquePartName[0]);
            setAppliedFilters(prev => ({ ...prev, partTypes: [uniquePartName[0]] }));
            setAppliedFiltersData(prev => ({ ...prev, partTypes: [uniquePartName[0]] }));
          }
        }
      } catch (error) {
        console.error('Error fetching finalised data:', error);
        // Set default values on error
        setRegions(['All', 'MAR', 'NAR', 'EUR']);
        setInterchangeNumbers(['All', '10007', '10008', '10009']);
        setPartTypes(['ENGINE', 'TRANSMISSION']);
        
        // Set Graph-specific dropdown options (without "All" option)
        setRegionsGraph(['MAR', 'NAR', 'EUR']);
        setInterchangeNumbersGraph(['10007', '10008', '10009']);
        
        // Set first available part type as default on error
        const defaultPartTypes = ['ENGINE', 'TRANSMISSION'];
        if (defaultPartTypes.length > 0) {
          setSelectedPartType(defaultPartTypes[0]);
          setPartTypeSearch(defaultPartTypes[0]);
          setAppliedFilters(prev => ({ ...prev, partTypes: [defaultPartTypes[0]] }));
          setAppliedFiltersData(prev => ({ ...prev, partTypes: [defaultPartTypes[0]] }));
        }
      
      } finally {
        setLoading(false);
      }
    };

    fetchFinalisedData();
  }, []);

  // Remove the automatic graph data fetching on component mount
  // Graph data will now be fetched only when Review Graph tab is clicked

  // Function to fetch graph data with current filters
  const fetchGraphDataWithFilters = async (forceRefresh = false) => {
    try {
      setGraphLoading(true);
      console.log('Fetching graph data with filters...');
      
      // Always fetch from API when switching to graph tab or when forceRefresh is true
      if (!originalGraphData || Object.keys(originalGraphData).length === 0 || forceRefresh) {
        const response = await apiService.GetBatchGraphData();
        console.log('Graph data response:', response);
        
        if (response && response.data) {
          setOriginalGraphData(response.data);
          console.log('Original graph data stored');
        } else {
          console.log('No graph data in response');
          setOriginalGraphData({});
          setGraphData({});
          return;
        }
      }
      
      // Apply filters to the original data
      let filteredGraphData = { ...originalGraphData };
      
      // Apply filters if they are selected
      if (appliedFilters.partTypes.length > 0 || appliedFilters.regions.length > 0 || appliedFilters.interchangeNumbers.length > 0) {
        console.log('Applying filters to graph data:', appliedFilters);
        
        // Filter inventory_pe data
        if (filteredGraphData.inventory_pe && Array.isArray(filteredGraphData.inventory_pe)) {
          filteredGraphData.inventory_pe = filteredGraphData.inventory_pe.filter(item => {
            let matches = true;
            
            // Filter by part type
            if (appliedFilters.partTypes.length > 0) {
              matches = matches && appliedFilters.partTypes.some(partType => item.part_name === partType);
            }
            
            // Filter by region
            if (appliedFilters.regions.length > 0) {
              matches = matches && appliedFilters.regions.some(region => item.region === region);
            }
            
            // Filter by IC number
            if (appliedFilters.interchangeNumbers.length > 0) {
              matches = matches && appliedFilters.interchangeNumbers.some(icNumber => item.interchange_number === icNumber);
            }
            
            return matches;
          });
        }
        
        // Filter sales data
        if (filteredGraphData.sales && Array.isArray(filteredGraphData.sales)) {
          filteredGraphData.sales = filteredGraphData.sales.filter(item => {
            let matches = true;
            
            // Filter by part type
            if (appliedFilters.partTypes.length > 0) {
              matches = matches && appliedFilters.partTypes.some(partType => item.part_name === partType);
            }
            
            // Filter by region
            if (appliedFilters.regions.length > 0) {
              matches = matches && appliedFilters.regions.some(region => item.region === region);
            }
            
            // Filter by IC number
            if (appliedFilters.interchangeNumbers.length > 0) {
              matches = matches && appliedFilters.interchangeNumbers.some(icNumber => item.interchange_number === icNumber);
            }
            
            return matches;
          });
        }
        
        console.log('Filtered graph data:', {
          inventory_pe: filteredGraphData.inventory_pe ? filteredGraphData.inventory_pe.length : 0,
          sales: filteredGraphData.sales ? filteredGraphData.sales.length : 0
        });
      }
      
      setGraphData(filteredGraphData);
      console.log('Graph data loaded successfully with filters applied');
    } catch (error) {
      console.error('Error fetching graph data:', error);
      setGraphData({});
    } finally {
      setGraphLoading(false);
    }
  };

  // Update dropdowns when part types change (including initial ENGINE selection)
  useEffect(() => {
    if (selectedPartType && finalisedData.length > 0) {
      updateDropdownsBasedOnPartTypes([selectedPartType]);
    }
  }, [selectedPartType, finalisedData]);



    const updateDropdownsBasedOnPartTypes = (selectedPartTypes) => {
    if (!finalisedData.length) return;

    console.log('Updating dropdowns based on Part Types:', selectedPartTypes);

    if (selectedPartTypes.includes('All')) {
      // If "All" is selected, show all regions and IC numbers
      const uniqueRegions = ['All', ...new Set(finalisedData.map(item => item.region).filter(Boolean))];
      const uniqueInterchanges = ['All', ...new Set(finalisedData.map(item => item.interchange_number).filter(Boolean))];
      setRegions(uniqueRegions);
      setInterchangeNumbers(uniqueInterchanges);
      
      // Update Graph-specific dropdowns (without "All" option)
      setRegionsGraph([...new Set(finalisedData.map(item => item.region).filter(Boolean))]);
      setInterchangeNumbersGraph([...new Set(finalisedData.map(item => item.interchange_number).filter(Boolean))]);
      
      console.log('Updated dropdowns for "All" - Regions:', uniqueRegions, 'IC Numbers:', uniqueInterchanges);
    } else {
      // Filter data by ALL selected part types (not just the first one)
      const filteredByPartTypes = finalisedData.filter(item => 
        selectedPartTypes.some(partType => item.part_name === partType)
      );
      console.log(`Filtered data for Part Types [${selectedPartTypes.join(', ')}]:`, filteredByPartTypes.length, 'items');
      
      // Extract unique regions and IC numbers from filtered data
      const uniqueRegions = ['All', ...new Set(filteredByPartTypes.map(item => item.region).filter(Boolean))];
      const uniqueInterchanges = ['All', ...new Set(filteredByPartTypes.map(item => item.interchange_number).filter(Boolean))];
      
      setRegions(uniqueRegions);
      setInterchangeNumbers(uniqueInterchanges);
      
      // Update Graph-specific dropdowns (without "All" option)
      setRegionsGraph([...new Set(filteredByPartTypes.map(item => item.region).filter(Boolean))]);
      setInterchangeNumbersGraph([...new Set(filteredByPartTypes.map(item => item.interchange_number).filter(Boolean))]);
      
      console.log(`Updated dropdowns for Part Types [${selectedPartTypes.join(', ')}] - Regions:`, uniqueRegions, 'IC Numbers:', uniqueInterchanges);
      
      // Log detailed breakdown of regions and IC numbers per part type
      selectedPartTypes.forEach(partType => {
        const partTypeData = finalisedData.filter(item => item.part_name === partType);
        const partTypeRegions = [...new Set(partTypeData.map(item => item.region).filter(Boolean))];
        const partTypeICNumbers = [...new Set(partTypeData.map(item => item.interchange_number).filter(Boolean))];
        console.log(`Part Type "${partType}":`, {
          count: partTypeData.length,
          regions: partTypeRegions,
          icNumbers: partTypeICNumbers
        });
      });      
    }
    // Don't reset selections automatically - let user choose
  };



    const updateICNumbersBasedOnRegion = (selectedRegions) => {
    if (!finalisedData.length) return;

    console.log('Updating IC Numbers based on Regions:', selectedRegions);

    if (selectedRegions.includes('All')) {
      // If "All" is selected, show IC numbers based on current part types filter
      let filteredData = finalisedData;
      
      if (selectedPartType && selectedPartType !== 'All') {
        filteredData = finalisedData.filter(item => 
          [selectedPartType].some(partType => item.part_name === partType)
        );
      }
      
      const uniqueInterchanges = ['All', ...new Set(filteredData.map(item => item.interchange_number).filter(Boolean))];
      setInterchangeNumbers(uniqueInterchanges);
      
      // Update Graph-specific dropdowns (without "All" option)
      setInterchangeNumbersGraph([...new Set(filteredData.map(item => item.interchange_number).filter(Boolean))]);
      
      console.log('Updated IC Numbers for "All" regions:', uniqueInterchanges);
    } else {
      // Filter data by selected regions and part types
      let filteredData = finalisedData.filter(item => 
        selectedRegions.some(region => item.region === region)
      );
      
      if (selectedPartType && selectedPartType !== 'All') {
        filteredData = filteredData.filter(item => 
          [selectedPartType].some(partType => item.part_name === partType)
        );
      }
      
      const uniqueInterchanges = ['All', ...new Set(filteredData.map(item => item.interchange_number).filter(Boolean))];
      setInterchangeNumbers(uniqueInterchanges);
      
      // Update Graph-specific dropdowns (without "All" option)
      setInterchangeNumbersGraph([...new Set(filteredData.map(item => item.interchange_number).filter(Boolean))]);
      
      console.log(`Updated IC Numbers for Regions [${selectedRegions.join(', ')}]:`, uniqueInterchanges);
    }
    
    // Don't reset IC number selection automatically - let user choose
  };

  // Handle clicking outside any searchable dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const containers = document.querySelectorAll('.searchable-select-container');
      let clickedInside = false;
      containers.forEach(c => { if (c.contains(event.target)) clickedInside = true; });
      if (!clickedInside) {
        setShowPartTypeDropdown(false);
        setShowRegionDropdown(false);
        setShowIcDropdown(false);
        setShowFinalizedByDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle multiselect dropdown selection with "All" logic (for Review Data tab)
  const handleSelect = (selectedList, selectedItem, setSelectedValues) => {
    console.log('Selected:', selectedList);
    
    // If "All" is selected, clear other selections
    if (selectedItem === 'All') {
      setSelectedValues(['All']);
      
      // Update dropdowns when "All" is selected
      if (setSelectedValues === setSelectedRegionsData) {
        updateICNumbersBasedOnRegion(['All']);
      }
    } else {
      // If a specific item is selected, remove "All" from the list
      const filteredList = selectedList.filter(item => item !== 'All');
      setSelectedValues(filteredList);
      
      // Update dropdowns when specific items are selected
      if (setSelectedValues === setSelectedRegionsData) {
        updateICNumbersBasedOnRegion(filteredList);
      }
    }
  };

  // Handle multiselect dropdown removal with "All" logic (for Review Data tab)
  const handleRemove = (selectedList, removedItem, setSelectedValues, options) => {
    console.log('Removed:', selectedList);
    
    // If "All" is being removed, do nothing (keep "All" selected)
    if (removedItem === 'All') {
      return;
    }
    
    // If no items are left after removal, select "All"
    if (selectedList.length === 0) {
      setSelectedValues(['All']);
      
      // Update dropdowns when "All" is selected
      if (setSelectedValues === setSelectedRegionsData) {
        updateICNumbersBasedOnRegion(['All']);
      }
    } else {
      setSelectedValues(selectedList);
      
      // Update dropdowns based on remaining selections
      if (setSelectedValues === setSelectedRegionsData && selectedList.length > 0) {
        updateICNumbersBasedOnRegion(selectedList);
      }
    }
  };

  const handleSearch = () => {
    if (activeTab === 'data') {
      // Handle Review Data tab (multi-select)
      console.log('Search button clicked for Review Data. Current filters:', {
        region: selectedPartType,
        regions: selectedRegionsData,
        interchangeNumbers: selectedInterchangeNumbersData,
        finalizedBy: selectedFinalizedBy,
        comparisonOperator: comparisonOperator,
        comparisonValue: comparisonValue
      });
      
      // Validate required selections for Review Data
      if (!selectedPartType || selectedPartType === '') {
        notificationService.showWarning('Please select a part type to view data.');
        return;
      }
      if (selectedRegionsData.length === 0) {
        notificationService.showWarning('Please select at least one region to view data.');
        return;
      }
      if (selectedInterchangeNumbersData.length === 0) {
        notificationService.showWarning('Please select at least one IC number to view data.');
        return;
      }
      
      // Apply the current selections as filters for Review Data
      setAppliedFiltersData({
        partTypes: selectedPartType ? [selectedPartType] : [],
        regions: selectedRegionsData,
        interchangeNumbers: selectedInterchangeNumbersData,
        finalizedBy: selectedFinalizedBy ? [selectedFinalizedBy] : [],
        comparisonOperator: comparisonOperator,
        comparisonValue: comparisonValue
      });
    } else {
      // Handle Review Graph tab (single-select)
      console.log('Search button clicked for Review Graph. Current filters:', {
        region: selectedRegion,
        partType: selectedPartType,
        interchangeNumber: selectedInterchangeNumber,
        finalizedBy: selectedFinalizedBy,
        comparisonOperator: comparisonOperator,
        comparisonValue: comparisonValue
      });
      
      // Validate required selections for Review Graph
      if (!selectedPartType || selectedPartType === '') {
        notificationService.showWarning('Please select a part type to view data.');
        return;
      }
      if (!selectedRegion || selectedRegion === '') {
        notificationService.showWarning('Please select a region to view data.');
        return;
      }
      if (!selectedInterchangeNumber || selectedInterchangeNumber === '') {
        notificationService.showWarning('Please select an IC number to view data.');
        return;
      }
      
      // Apply the current selections as filters for Review Graph
      setAppliedFilters({
        partTypes: selectedPartType ? [selectedPartType] : [],
        regions: selectedRegion ? [selectedRegion] : [],
        interchangeNumbers: selectedInterchangeNumber ? [selectedInterchangeNumber] : [],
        finalizedBy: selectedFinalizedBy ? [selectedFinalizedBy] : [],
        comparisonOperator: comparisonOperator,
        comparisonValue: comparisonValue
      });
      
      // Refresh the graph data with new filters and show loader
      setGraphLoading(true);
      fetchGraphDataWithFilters(false);
    }
    
    // Reset to first page when applying new filters
    setCurrentPage(1);
  };
    const toggleEditMode = () => {
    setIsEditModeEnabled(!isEditModeEnabled);
  };
 useEffect(() => {
    // This will trigger a re-render with updated statistics and charts
    // when applied filters change (after search button click)
    console.log('Applied filters changed:', appliedFilters);
    
    // If we're on the graph tab and have original graph data, refresh the filtered data
    if (activeTab === 'graph' && originalGraphData && Object.keys(originalGraphData).length > 0) {
      setGraphLoading(true);
      fetchGraphDataWithFilters(false);
    }
  }, [appliedFilters, activeTab, originalGraphData]);



  const getCurrentFilteredData = () => {
    // Use the appropriate filters based on active tab
    const currentFilters = activeTab === 'data' ? appliedFiltersData : appliedFilters;
    
    // Only show data if search has been performed (regions and IC numbers are selected)
    const hasSearched = currentFilters.regions.length > 0 && currentFilters.interchangeNumbers.length > 0;
    if (!hasSearched) {
      return []; // Return empty array if search hasn't been performed
    }
    let filteredData = finalisedData;
    
    // Filter by part type
    if (currentFilters.partTypes.length > 0 && !currentFilters.partTypes.includes('All')) {
      filteredData = filteredData.filter(item => 
        currentFilters.partTypes.some(partType => item.part_name === partType)
      );
    }
    
    // Filter by region
    if (currentFilters.regions.length > 0 && !currentFilters.regions.includes('All')) {
      filteredData = filteredData.filter(item => 
        currentFilters.regions.some(region => item.region === region)
      );
    }
    
    // Filter by IC number
    if (currentFilters.interchangeNumbers.length > 0 && !currentFilters.interchangeNumbers.includes('All')) {
      filteredData = filteredData.filter(item => 
        currentFilters.interchangeNumbers.some(icNumber => item.interchange_number === icNumber)
      );
    }
    
    // Filter by Finalized By
    if (currentFilters.finalizedBy && currentFilters.finalizedBy.length > 0) {
      filteredData = filteredData.filter(item => 
        currentFilters.finalizedBy.some(name => item.approved_by_name === name)
      );
    }
    
    // Filter by percentage difference - this should work independently
    if (currentFilters.comparisonValue !== '') {
      filteredData = filteredData.filter(item => {
        const originalPrice = parseFloat(item.original_price) || 0;
        const adjustedPrice = parseFloat(item.adjusted_price) || 0;
        const percentDiff = originalPrice > 0 
          ? ((adjustedPrice - originalPrice) / originalPrice) * 100
          : 0;
        if (currentFilters.comparisonOperator === 'greater') {
          return percentDiff > Number(currentFilters.comparisonValue);
        }
        if (currentFilters.comparisonOperator === 'less') {
          return percentDiff < Number(currentFilters.comparisonValue);
        }
        return true;
      });
    }
    
    // Apply user region access filtering
    const userAccessFiltered = filteredData.filter(item => {
      const userRegionAccess = filterDataByUserAccess([item], user, 'region');
      return userRegionAccess.length > 0;
    });
    return userAccessFiltered;
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to filtered data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle numeric values
      const numericFields = ['mileage', 'original_price', 'adjusted_price', 'dollar_positive_perc_limit', 'dollar_negative_perc_limit', 'model_predicated_price'];
      if (numericFields.includes(sortConfig.key)) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      // Handle calculated fields that need special processing
      if (sortConfig.key === 'price_difference') {
        const aOriginalPrice = parseFloat(a.original_price) || 0;
        const aAdjustedPrice = parseFloat(a.adjusted_price) || 0;
        const bOriginalPrice = parseFloat(b.original_price) || 0;
        const bAdjustedPrice = parseFloat(b.adjusted_price) || 0;
        aValue = aAdjustedPrice - aOriginalPrice;
        bValue = bAdjustedPrice - bOriginalPrice;
      }
      
      if (sortConfig.key === 'percentage_diff') {
        const aOriginalPrice = parseFloat(a.original_price) || 0;
        const aAdjustedPrice = parseFloat(a.adjusted_price) || 0;
        const bOriginalPrice = parseFloat(b.original_price) || 0;
        const bAdjustedPrice = parseFloat(b.adjusted_price) || 0;
        aValue = aOriginalPrice > 0 ? ((aAdjustedPrice - aOriginalPrice) / aOriginalPrice) * 100 : 0;
        bValue = bOriginalPrice > 0 ? ((bAdjustedPrice - bOriginalPrice) / bOriginalPrice) * 100 : 0;
      }
      
      // Handle date values
      if (sortConfig.key === 'created_on') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // Handle string values
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const filteredData = getCurrentFilteredData();
  const sortedData = getSortedData(filteredData);

  // Derived counts for tab badges (must come after filteredData)
  const dataCount = useMemo(() => filteredData?.length || 0, [filteredData]);
  const graphPointCount = useMemo(() => {
    try {
      const traces = prepareUnifiedChartData();
      return Array.isArray(traces) ? traces.reduce((sum, t) => sum + (t?.x?.length || 0), 0) : 0;
    } catch {
      return 0;
    }
  }, [graphData, appliedFilters]);

  // Filtered option lists for searchable dropdowns (single-select for Graph tab)
  const filteredPartTypeOptions = useMemo(() => {
    if (!partTypeSearch.trim()) return partTypes;
    return partTypes.filter(pt => String(pt).toLowerCase().includes(partTypeSearch.toLowerCase()));
  }, [partTypes, partTypeSearch]);
  const filteredRegionOptions = useMemo(() => {
    if (!regionSearch.trim()) return regionsGraph;
    return regionsGraph.filter(r => String(r).toLowerCase().includes(regionSearch.toLowerCase()));
  }, [regionsGraph, regionSearch]);
  const filteredIcOptions = useMemo(() => {
    if (!icSearch.trim()) return interchangeNumbersGraph;
    return interchangeNumbersGraph.filter(ic => String(ic).toLowerCase().includes(icSearch.toLowerCase()));
  }, [interchangeNumbersGraph, icSearch]);
  const filteredFinalizedByOptions = useMemo(() => {
    if (!finalizedBySearch.trim()) return finalizedByNames;
    return finalizedByNames.filter(name => String(name).toLowerCase().includes(finalizedBySearch.toLowerCase()));
  }, [finalizedByNames, finalizedBySearch]);


  // Memoized regression points from filtered sales data (mileage vs predictedPrice)
  const regressionPoints = useMemo(() => {
    try {
      // Ensure filters are applied (require region and IC selections similar to chart visibility)
      const hasSearched = appliedFilters.regions.length > 0 && appliedFilters.interchangeNumbers.length > 0;
      if (!hasSearched) return [];

      // Determine graph data structure and get sales
      const actualGraphData = graphData && graphData.data ? graphData.data : graphData;
      const sales = actualGraphData && Array.isArray(actualGraphData.sales) ? actualGraphData.sales : [];
      if (sales.length === 0) return [];

      // Apply same filters as chart for consistency
      let source = sales.filter(item => Number(item.mileage) > 0 && item.sold_price !== null && item.sold_price !== undefined);

      if (appliedFilters.partTypes.length > 0) {
        source = source.filter(item => appliedFilters.partTypes.some(p => item.part_name === p));
      }
      if (appliedFilters.regions.length > 0) {
        source = source.filter(item => appliedFilters.regions.some(r => item.region === r));
      }
      if (appliedFilters.interchangeNumbers.length > 0) {
        source = source.filter(item => appliedFilters.interchangeNumbers.some(ic => item.interchange_number === ic));
      }

      if (source.length < 2) return [];

      // Align mileage scale with chart (sold_price mileage multiplied by 1000 in chart)
      const x = source.map(s => (parseFloat(s.mileage) || 0) * 1000);
      const y = source.map(s => parseFloat(s.sold_price) || 0);

      const coeff = calculateExponentialRegression(x, y);
      if (!coeff) return [];

      // Find min and max mileage values from filtered data
      const validX = x.filter(v => Number.isFinite(v));
      if (validX.length === 0) return [];

      const salesMinMileage = Math.min(...validX);
      const salesMaxMileage = Math.max(...validX);

      // Also get mileage range from inventory_pe data for comparison
      const inventoryData = actualGraphData && Array.isArray(actualGraphData.inventory_pe) ? actualGraphData.inventory_pe : [];
      
      let inventoryMinMileage = null;
      let inventoryMaxMileage = null;
      
      if (inventoryData.length > 0) {
        // Apply same filters to inventory data
        let filteredInventoryData = inventoryData;
        
        if (appliedFilters.partTypes.length > 0) {
          filteredInventoryData = filteredInventoryData.filter(item => 
            appliedFilters.partTypes.some(partType => item.part_name === partType)
          );
        }
        if (appliedFilters.regions.length > 0) {
          filteredInventoryData = filteredInventoryData.filter(item => 
            appliedFilters.regions.some(region => item.region === region)
          );
        }
        if (appliedFilters.interchangeNumbers.length > 0) {
          filteredInventoryData = filteredInventoryData.filter(item => 
            appliedFilters.interchangeNumbers.some(icNumber => item.interchange_number === icNumber)
          );
        }
        
        // Get valid mileage values from inventory data
        const inventoryMileages = filteredInventoryData
          .filter(item => Number(item.mileage) > 0)
          .map(item => parseFloat(item.mileage) || 0)
          .filter(v => Number.isFinite(v));
          
        if (inventoryMileages.length > 0) {
          inventoryMinMileage = Math.min(...inventoryMileages);
          inventoryMaxMileage = Math.max(...inventoryMileages);
        }
      }

      // Compare sales and inventory mileage ranges to get overall min and max
      const minMileage = inventoryMinMileage !== null 
        ? Math.min(salesMinMileage, inventoryMinMileage)
        : salesMinMileage;
        
      const maxMileage = inventoryMaxMileage !== null 
        ? Math.max(salesMaxMileage, inventoryMaxMileage)
        : salesMaxMileage;

      console.log('Mileage range comparison:', {
        salesRange: { min: salesMinMileage, max: salesMaxMileage },
        inventoryRange: { min: inventoryMinMileage, max: inventoryMaxMileage },
        finalRange: { min: minMileage, max: maxMileage }
      });

      // Check if we have at least 2 different mileage points (not just a single point)
      if (minMileage === maxMileage) {
        console.log('Only single mileage point available, skipping regression line generation');
        return [];
      }

        // Generate points at 2000-mile intervals from start to end
      const intervalPoints = [];
      const interval = 2000; // 2000-mile intervals
      
      // Start from the minimum mileage and go up to maximum mileage in 2000-mile steps
      for (let mileage = minMileage; mileage <= maxMileage; mileage += interval) {
        const predictedPrice = coeff.a * Math.exp(coeff.b * mileage);
        intervalPoints.push({ 
          mileage: mileage, 
          predictedPrice: predictedPrice 
        });
      }

      // Always include the maximum mileage point if it's not already included
      const lastPoint = intervalPoints[intervalPoints.length - 1];
      if (lastPoint && lastPoint.mileage < maxMileage) {
        const finalPredictedPrice = coeff.a * Math.exp(coeff.b * maxMileage);
        intervalPoints.push({ 
          mileage: maxMileage, 
          predictedPrice: finalPredictedPrice 
        });
      }

      console.log('Generated regression points:', {
        minMileage,
        maxMileage,
        interval,
        pointCount: intervalPoints.length,
        points: intervalPoints.map(p => ({ 
          mileage: Math.round(p.mileage), 
          predictedPrice: Math.round(p.predictedPrice * 100) / 100 
        }))
      });

      return intervalPoints;
    } catch (error) {
      console.error('Error generating regression points:', error);
      return [];
    }
  }, [graphData, appliedFilters]);

  // Clear graph data when all filters are cleared
  useEffect(() => {
    if (appliedFilters.regions.length === 0 || appliedFilters.interchangeNumbers.length === 0) {
      setGraphData({});
    }
  }, [appliedFilters.regions, appliedFilters.interchangeNumbers]);





//   const handlePriceChange = async (id, newValue) => {
//     try {
//       const numericValue = parseFloat(newValue);
      
//       // Validate input
//       if (isNaN(numericValue) || numericValue < 0) {
//         notificationService.showError('Please enter a valid positive number');
//         return;
//       }
      
//       // Update the local state
//       const updatedData = finalisedData.map(item => 
//         item.id === id 
//           ? { ...item, adjusted_price: numericValue }
//           : item
//       );
//       setFinalisedData(updatedData);
      
//       // Check if there are unsaved changes
//       const hasUnsaved = updatedData.some((item, index) => {
//         const original = originalData[index];
//         return original && original.adjusted_price !== item.adjusted_price;
//       });
//       setHasChanges(hasUnsaved);
      
//       // Call the batchexpertqueue controller to update the batch
//       try {
//         const updatedItem = updatedData.find(item => item.id === id);
       
//         if (updatedItem) {
//           //  Prepare the batch data for the API call
//            const batchData = {
//            id: updatedItem.id,
//             adjusted_price: updatedItem.adjusted_price,
       
//            };

       
//  await apiService.UpdateBatch([batchData]);
          
//           // Call the UpdateBatch API method from batchexpertqueue controller
         

          
//           // Log success for debugging (only in development)
//           if (process.env.NODE_ENV === 'development') {
//             console.log('Successfully updated batch expert queue for item:', updatedItem.id);
//           }
//         }
//       } catch (batchError) {
//         console.error('Error updating batch expert queue:', batchError);
//         // Don't show error to user for batch queue update - it's a background process
//         // The main price change functionality should still work
//       }
      
//     } catch (error) {
//       console.error('Error updating adjusted price:', error);
//       notificationService.showError('Failed to update adjusted price');
//     }
//   };

  //checks any one value is changed or not otherwise return false
  const handlePriceChange = async (id, newValue) => {
    try {
      // Check if user has Super Admin role
      if (!isSuperAdmin) {
        notificationService.showError('Only Super Admin users can edit prices');
        return;
      }
  
      const numericValue = parseFloat(newValue);
  
      // Validate input
      if (isNaN(numericValue) || numericValue < 0) {
        notificationService.showError('Please enter a valid positive number');
        return;
      }
  
      // Find the original item to compare
      const originalItem = originalData.find(item => item.id === id);
      if (!originalItem) return;
  
      // Convert both values to numbers for proper comparison
      const originalPrice = parseFloat(originalItem.adjusted_price);
      const newPrice = parseFloat(numericValue);
  
      // Only update if the value actually changed
      if (originalPrice === newPrice) {
        console.log('No change detected:', originalPrice, '===', newPrice);
        return; // No change, don't update anything
      }
  
      console.log('Change detected:', originalPrice, '!==', newPrice);
  
      // Calculate new price difference and percentage difference
      const item = finalisedData.find(item => item.id === id);
      if (item) {
        const originalItemPrice = parseFloat(item.original_price) || 0;
        const newPriceDifference = numericValue - originalItemPrice;
        const newPercentageDifference = originalItemPrice > 0 
          ? (((numericValue - originalItemPrice) / originalItemPrice) * 100).toFixed(2)
          : '0.00';
  
        // Update local state with new values
        const updatedData = finalisedData.map(item =>
          item.id === id ? { 
            ...item, 
            adjusted_price: numericValue,
            price_Difference: newPriceDifference,
            percentage_Difference: parseFloat(newPercentageDifference)
          } : item
        );
        setFinalisedData(updatedData);
  
        // Check for unsaved changes
        const hasUnsaved = updatedData.some((item, index) => {
          const original = originalData[index];
          return original && original.adjusted_price !== item.adjusted_price;
        });
        setHasChanges(hasUnsaved);
      }
    } catch (error) {
      console.error('Error updating price:', error);
      notificationService.showError('Failed to update adjusted price');
    }
  };
  




  
  const hasUnsavedChanges = () => {
    if (originalData.length === 0 || finalisedData.length === 0) return false;
    
    return finalisedData.some((item, index) => {
      const original = originalData[index];
      return original && original.adjusted_price !== item.adjusted_price;
    });
  };











  //Returns the number of element changed
  const getUnsavedChangesCount = () => {
    if (originalData.length === 0 || finalisedData.length === 0) return 0;
    
    return finalisedData.filter((item, index) => {
      const original = originalData[index];
      return original && original.adjusted_price !== item.adjusted_price;
    }).length;
  };






  const handleSaveAllChanges = async () => {
    try {

      if (!isSuperAdmin) {
        notificationService.showError('Only Super Admin users can save changes');
        return;
      }
      if (!hasUnsavedChanges()) {
        notificationService.showInfo('No changes to save');
        return;
      }

      // Find all items that have been changed
      const changedItems = finalisedData.filter((item, index) => {
        const original = originalData[index];
        return original && original.adjusted_price !== item.adjusted_price;
      });

      if (changedItems.length === 0) {
        notificationService.showInfo('No changes to save');
        return;
      }
             
  // Show confirmation dialog
  // const confirmMessage = `Are you sure you want to save ${changedItems.length} changes?\n\nThis will update the adjusted prices for:\n${changedItems.slice(0, 3).map(item => `• ${item.part_name}: $${formatNumber(item.adjusted_price)}`).join('\n')}${changedItems.length > 3 ? `\n... and ${changedItems.length - 3} more items` : ''}`;
      
  // if (!window.confirm(confirmMessage)) {
  //   return;
  // }

      setSaving(true);

      // Here you would typically make API calls to save all changes
      // const promises = changedItems.map(item => 
      //   apiService.updateAdjustedPrice(item.id, item.adjusted_price)
      // );
      // await Promise.all(promises);

      // Simulate API delay for demo purposes
      await new Promise(resolve => setTimeout(resolve, 1000));
      const batchData = changedItems.map(item => {
        // Use the updated values from the modified item
        const priceDifference = item.price_Difference !== undefined 
          ? item.price_Difference 
          : parseFloat(item.adjusted_price) - parseFloat(item.original_price);
        
        const percentageDifference = item.percentage_Difference !== undefined
          ? item.percentage_Difference
          : (parseFloat(item.original_price) > 0
            ? (((parseFloat(item.adjusted_price) - parseFloat(item.original_price)) / parseFloat(item.original_price)) * 100)
            : 0);
        
        return {
          ipe_id: item.id,
          adjusted_price: item.adjusted_price,
          price_difference: priceDifference,
          percentage_difference: percentageDifference,
          approved_by: user?.first_name && user?.last_name 
            ? `${user.first_name} ${user.last_name}` 
            : user?.first_name || user?.last_name || null,
          approved_by_id: user?.id || null
        };
      });
      await apiService.UpdateBatch(batchData);
      setOriginalData([...finalisedData]);
      setHasChanges(false);
      setIsEditModeEnabled(false);
      // // Call GET API to refresh data
      // const response = await apiService.GetFinalisedData();
      // if (response && response.data) {
      //   setFinalisedData(response.data);
      //   setOriginalData([...response.data]);
        
      //   // Update dropdown options with fresh data
      //   const uniqueApprovedBy = ['All', ...new Set(response.data.map(item => item.approved_by_name).filter(Boolean))];
      //   setApprovedByNames(uniqueApprovedBy);
      // }

      notificationService.showSuccess(`Successfully saved ${changedItems.length} changes`);

      // Log the batchData for debugging (only in development)
      if (process.env.NODE_ENV === 'development') {
        console.log('Saved batchData:', batchData);
      }

    } catch (error) {
      console.error('Error saving all changes:', error);
      notificationService.showError('Failed to save some changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };













  // Monitor changes in finalisedData to update hasChanges state
  useEffect(() => {
    if (originalData.length > 0 && finalisedData.length > 0) {
      const hasUnsaved = finalisedData.some((item, index) => {
        const original = originalData[index];
        return original && original.adjusted_price !== item.adjusted_price;
      });
      setHasChanges(hasUnsaved);
    }
  }, [finalisedData, originalData]);






const totalItems = sortedData.length;
const totalPages = Math.ceil(totalItems / itemsPerPage);
const startIndex = (currentPage - 1) * itemsPerPage;
const endIndex = startIndex + itemsPerPage;
const currentData = sortedData.slice(startIndex, endIndex);



  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleItemsPerPageChange = (event) => {
    // Note: itemsPerPage is currently fixed at 10
    // To enable dynamic items per page, uncomment the line below and change the state declaration
    // setItemsPerPage(parseInt(event.target.value));
    setCurrentPage(1);
  };





  // Render pagination controls (matching All Interchanges style)
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        ←
      </button>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key="1"
          className="pagination-btn"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          className="pagination-btn"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        →
      </button>
    );

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          <div className="d-flex align-items-center">
            <span>Show:</span>
            <span className="mx-2 fw-bold">{itemsPerPage}</span>
            <span>entries</span>
          </div>
          <span className="pagination-info-text">
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
          </span>
        </div>
        <div className="pagination-controls">
          {pages}
        </div>
      </div>
    );
  };








  if (loading) {
    return (
      <div style={{
        position: 'fixed',   
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(255,255,255,0.8)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div className="bouncing-loader">
          <div></div><div></div><div></div>
        </div>
        <style>
          {`
          .bouncing-loader {
            display: flex;
            align-items: flex-end;
            justify-content: center;
            height: 60px;
            gap: 10px;
          }
          .bouncing-loader div {
            width: 18px;
            height: 18px;
            background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
            border-radius: 50%;
            animation: bouncing-loader 0.6s infinite alternate;
            box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
          }
          .bouncing-loader div:nth-child(2) {
            animation-delay: 0.2s;
          }
          .bouncing-loader div:nth-child(3) {
            animation-delay: 0.4s;
          }
          @keyframes bouncing-loader {
            to {
              transform: translateY(-30px) scale(1.1);
              box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
            }
          }
          `}
        </style>
      </div>
    );
  }




  
  return (
    <div className="">
      {/* Tabs: Review Data | Review Graph */}
      <div
        className="review-tabs mb_15"
        role="tablist"
        aria-label="Review switcher"
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            setActiveTab(prev => (prev === 'data' ? 'graph' : 'data'));
          }
        }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'data'}
          className={`review-tab ${activeTab === 'data' ? 'active' : ''}`}
          onClick={() => setActiveTab('data')}
        >
          Review Data 
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'graph'}
          className={`review-tab ${activeTab === 'graph' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('graph');
            // Always fetch fresh graph data when switching to graph tab
            setGraphLoading(true);
            fetchGraphDataWithFilters(true);
          }}
        >
          Review Graph 
        </button>
      </div>

      {/* Filter Section - Different for each tab */}
      {activeTab === 'data' ? (
        // Review Data Tab - Multi-select filters
        <div className="custom_filter_section filter_section d-flex align-items-start row mb_25" style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '20px', 
          borderRadius: '12px', 
          border: '1px solid #dee2e6',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>

          <div className="filter_items col-md-3">
            <label className="font_14 fw-med text_black">
              Part Type <span className="text-danger">*</span>
            </label>
            <div className="searchable-select-container">
              <div className="input-wrapper">
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search Part Type..."
                  value={partTypeSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPartTypeSearch(value);
                    if (selectedPartType && value !== selectedPartType) {
                      setSelectedPartType('');
                      setSelectedRegionsData(['All']);
                      setSelectedInterchangeNumbersData(['All']);
                    }
                  }}
                  onFocus={() => setShowPartTypeDropdown(true)}
                />
                {(selectedPartType || partTypeSearch) && (
                  <button
                    type="button"
                    className="clear-button"
                    onClick={() => {
                      setSelectedPartType('');
                      setPartTypeSearch('');
                      setShowPartTypeDropdown(false);
                      setSelectedRegionsData(['All']);
                      setSelectedInterchangeNumbersData(['All']);
                    }}
                    title="Clear selection"
                  >
                    ✕
                  </button>
                )}
              </div>
              {showPartTypeDropdown && (
                <div className="searchable-dropdown">
                  <div className="dropdown-list">
                    {filteredPartTypeOptions.length > 0 ? (
                      filteredPartTypeOptions.map((type, index) => (
                        <div
                          key={index}
                          className={`dropdown-item ${selectedPartType === type ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedPartType(type);
                            setPartTypeSearch(type);
                            setShowPartTypeDropdown(false);
                            setSelectedRegionsData(['All']);
                            setSelectedInterchangeNumbersData(['All']);
                            updateDropdownsBasedOnPartTypes([type]);
                          }}
                        >
                          {type}
                          {selectedPartType === type && <span className="selected-indicator">✓</span>}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item no-results">No Part Types found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div> 

          <div className="filter_items col-md-3">
            <label className="font_14 fw-med text_black">
              Regions <span className="text-danger">*</span>
              {!selectedRegionsData.includes('All') && selectedRegionsData.length > 0 && (
                <span className="badge bg-primary ms-2" style={{ fontSize: '10px' }}>
                  {selectedRegionsData.length} selected
                </span>
              )}
            </label>
            <Multiselect
              isObject={false}
              options={regions}
              selectedValues={selectedRegionsData}
              onSelect={(selectedList, selectedItem) => {
                handleSelect(selectedList, selectedItem, setSelectedRegionsData);
              }}
              onRemove={(selectedList, removedItem) => {
                handleRemove(selectedList, removedItem, setSelectedRegionsData, regions);
              }}
              placeholder="Select Regions"
            />
          </div>
         
          <div className="filter_items col-md-3">
            <label className="font_14 fw-med text_black">
              IC Number <span className="text-danger">*</span>
              {!selectedInterchangeNumbersData.includes('All') && selectedInterchangeNumbersData.length > 0 && (
                <span className="badge bg-primary ms-2" style={{ fontSize: '10px' }}>
                  {selectedInterchangeNumbersData.length} selected
                </span>
              )}
            </label>
            <Multiselect
              isObject={false}
              options={interchangeNumbers}
              selectedValues={selectedInterchangeNumbersData}
              onSelect={(selectedList, selectedItem) => {
                handleSelect(selectedList, selectedItem, setSelectedInterchangeNumbersData);
              }}
              onRemove={(selectedList, removedItem) => {
                handleRemove(selectedList, removedItem, setSelectedInterchangeNumbersData, interchangeNumbers);
              }}
              placeholder="Select IC Number"
            />
          </div>

        {/* Finalized By Dropdown */}
        <div className="filter_items col-md-3">
          <label className="font_14 fw-med text_black">
          Approved By
          </label>
          <div className="searchable-select-container">
            <div className="input-wrapper">
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search Approved By..."
                value={finalizedBySearch}
                onChange={(e) => {
                  const value = e.target.value;
                  setFinalizedBySearch(value);
                  if (selectedFinalizedBy && value !== selectedFinalizedBy) {
                    setSelectedFinalizedBy('');
                  }
                }}
                onFocus={() => setShowFinalizedByDropdown(true)}
                onDoubleClick={() => {
                  if (selectedFinalizedBy) {
                    setFinalizedBySearch(selectedFinalizedBy);
                    setSelectedFinalizedBy('');
                    setShowFinalizedByDropdown(true);
                  }
                }}
              />
              {(selectedFinalizedBy || finalizedBySearch) && (
                <button
                  type="button"
                  className="clear-button"
                  onClick={() => {
                    setSelectedFinalizedBy('');
                    setFinalizedBySearch('');
                    setShowFinalizedByDropdown(false);
                  }}
                  title="Clear selection"
                >
                  ✕
                </button>
              )}
            </div>
            {showFinalizedByDropdown && (
              <div className="searchable-dropdown">
                <div className="dropdown-list">
                  {filteredFinalizedByOptions.length > 0 ? (
                    filteredFinalizedByOptions.map((name, index) => (
                      <div
                        key={index}
                        className={`dropdown-item ${selectedFinalizedBy === name ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedFinalizedBy(name);
                          setFinalizedBySearch(name);
                          setShowFinalizedByDropdown(false);
                        }}
                      >
                        {name}
                        {selectedFinalizedBy === name && <span className="selected-indicator">✓</span>}
                      </div>
                    ))
                  ) : (
                    <div className="dropdown-item no-results">No names found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
     {/* <div className='filter_items col-md-3'>
          <label className='font_14 fw-med text_black'>Comparision</label>
        
        <div className=" multiselect-container multiSelectContainer d-flex align-items-center search-wrapper searchWrapper comparision_blk">
          <select id="comparison" name="comparison" className='searchBox select_custom'>
            <option value="greater">Greater Than</option>
            <option value="less">Less Than</option>
          </select>
          <input type="text" className='searchBox input_custom w-100px'></input>
        </div> 
        </div>  */}
        
        <div className='filter_items col-md-3'>
  <label className='font_14 fw-med text_black'>% difference</label>

  <div className="d-flex flex-column gap-2">
    {/* Dropdown for Greater / Less with arrow */}
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <select
        id="comparison"
        name="comparison"
        className='form-control'
        value={comparisonOperator}
        onChange={(e) => setComparisonOperator(e.target.value)}
        style={{ 
          padding: '8px 12px',
          paddingRight: '30px',
          fontSize: '14px',
          border: '1px solid #ddd',
          borderRadius: '4px 0px 0px 4px',
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none'
        }}
      >
        <option value="greater">Greater Than</option>
        <option value="less">Less Than</option>
      </select>
      <img 
        src={dropdownArrow} 
        alt="dropdown arrow" 
        style={{ 
          position: 'absolute', 
          right: '10px', 
          width: '12px', 
          height: '12px',
          pointerEvents: 'none'
        }} 
      />
    </div>

    {/* Percentage input with % symbol */}
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '120px' }}>
      <input
        type="number"
        step="0.01"
        className='form-control'
        value={comparisonValue}
        onChange={(e) => setComparisonValue(e.target.value)}
        placeholder="0.2"
        style={{ 
          paddingRight: '25px',
          fontSize: '14px',
          padding: '8px 12px',
          borderLeft: 'none',
          borderRadius: '0px 4px 4px 0',
          width: '100%'
        }}
      />
      <span style={{ 
        position: 'absolute', 
        right: '10px', 
        fontSize: '14px', 
        color: '#666',
        pointerEvents: 'none',
        fontWeight: 'bold'
      }}>%</span>
    </div>
  </div>
</div>

        <div className="filter_button_blk mt_26" style={{marginLeft: '10px'}}>
           <img 
             src={searchIcon} 
             alt="search" 
             className="" 
             onClick={handleSearch}
             style={{ 
               cursor: 'pointer',
               transition: 'transform 0.2s'
             }}
             onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
             onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
           />
           </div>
        </div>
      ) : (
        // Review Graph Tab - Single-select filters (existing code)
        <div className="quartile-config-panel mt-4">
        <div className="custom_filter_section d-flex align-items-start row">
          <div className="filter_items col-md-3">
            <label className="font_14 fw-med text_black">
              Part Type <span className="text-danger">*</span>
            </label>
            <div className="searchable-select-container">
              <div className="input-wrapper">
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search Part Type..."
                  value={partTypeSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPartTypeSearch(value);
                    if (selectedPartType && value !== selectedPartType) {
                      setSelectedPartType('');
                      setSelectedRegion('');
                      setRegionSearch('');
                      setSelectedInterchangeNumber('');
                      setIcSearch('');
                    }
                  }}
                  onFocus={() => setShowPartTypeDropdown(true)}
                />
                {(selectedPartType || partTypeSearch) && (
                  <button
                    type="button"
                    className="clear-button"
                    onClick={() => {
                      setSelectedPartType('');
                      setPartTypeSearch('');
                      setShowPartTypeDropdown(false);
                      setSelectedRegion('');
                      setRegionSearch('');
                      setSelectedInterchangeNumber('');
                      setIcSearch('');
                    }}
                    title="Clear selection"
                  >
                    ✕
                  </button>
                )}
              </div>
              {showPartTypeDropdown && (
                <div className="searchable-dropdown">
                  <div className="dropdown-list">
                    {filteredPartTypeOptions.length > 0 ? (
                      filteredPartTypeOptions.map((type, index) => (
                        <div
                          key={index}
                          className={`dropdown-item ${selectedPartType === type ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedPartType(type);
                            setPartTypeSearch(type);
                            setShowPartTypeDropdown(false);
                            setSelectedRegion('');
                            setRegionSearch('');
                            setSelectedInterchangeNumber('');
                            setIcSearch('');
                            // Update Graph-specific dropdowns based on selected part type
                            const filteredData = finalisedData.filter(item => item.part_name === type);
                            const uniqueRegions = [...new Set(filteredData.map(item => item.region).filter(Boolean))];
                            const uniqueInterchanges = [...new Set(filteredData.map(item => item.interchange_number).filter(Boolean))];
                            setRegionsGraph(uniqueRegions);
                            setInterchangeNumbersGraph(uniqueInterchanges);
                          }}
                        >
                          {type}
                          {selectedPartType === type && <span className="selected-indicator">✓</span>}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item no-results">No Part Types found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="filter_items col-md-3">
            <label className="font_14 fw-med text_black">
              Regions <span className="text-danger">*</span>
            </label>
            <div className="searchable-select-container">
              <div className="input-wrapper">
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search Region..."
                  value={regionSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setRegionSearch(value);
                    if (selectedRegion && value !== selectedRegion) {
                      setSelectedRegion('');
                      setSelectedInterchangeNumber('');
                      setIcSearch('');
                    }
                  }}
                  onFocus={() => setShowRegionDropdown(true)}
                />
                {(selectedRegion || regionSearch) && (
                  <button
                    type="button"
                    className="clear-button"
                    onClick={() => {
                      setSelectedRegion('');
                      setRegionSearch('');
                      setShowRegionDropdown(false);
                      setSelectedInterchangeNumber('');
                      setIcSearch('');
                    }}
                    title="Clear selection"
                  >
                    ✕
                  </button>
                )}
              </div>
              {showRegionDropdown && (
                <div className="searchable-dropdown">
                  <div className="dropdown-list">
                    {filteredRegionOptions.length > 0 ? (
                      filteredRegionOptions.map((r, index) => (
                        <div
                          key={index}
                          className={`dropdown-item ${selectedRegion === r ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedRegion(r);
                            setRegionSearch(r);
                            setShowRegionDropdown(false);
                            setSelectedInterchangeNumber('');
                            setIcSearch('');
                            // Update IC numbers based on selected region (Review Graph tab)
                            const filteredData = finalisedData.filter(item => item.region === r);
                            if (selectedPartType && selectedPartType !== 'All') {
                              const furtherFiltered = filteredData.filter(item => item.part_name === selectedPartType);
                              const uniqueInterchanges = [...new Set(furtherFiltered.map(item => item.interchange_number).filter(Boolean))];
                              setInterchangeNumbersGraph(uniqueInterchanges);
                            } else {
                              const uniqueInterchanges = [...new Set(filteredData.map(item => item.interchange_number).filter(Boolean))];
                              setInterchangeNumbersGraph(uniqueInterchanges);
                            }
                          }}
                        >
                          {r}
                          {selectedRegion === r && <span className="selected-indicator">✓</span>}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item no-results">No Regions found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
           
          <div className="filter_items col-md-3">
            <label className="font_14 fw-med text_black">
              IC Number <span className="text-danger">*</span>
            </label>
            <div className="searchable-select-container">
              <div className="input-wrapper">
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search IC Number..."
                  value={icSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setIcSearch(value);
                    if (selectedInterchangeNumber && value !== selectedInterchangeNumber) {
                      setSelectedInterchangeNumber('');
                    }
                  }}
                  onFocus={() => setShowIcDropdown(true)}
                />
                {(selectedInterchangeNumber || icSearch) && (
                  <button
                    type="button"
                    className="clear-button"
                    onClick={() => {
                      setSelectedInterchangeNumber('');
                      setIcSearch('');
                      setShowIcDropdown(false);
                    }}
                    title="Clear selection"
                  >
                    ✕
                  </button>
                )}
              </div>
              {showIcDropdown && (
                <div className="searchable-dropdown">
                  <div className="dropdown-list">
                    {filteredIcOptions.length > 0 ? (
                      filteredIcOptions.map((ic, index) => (
                        <div
                          key={index}
                          className={`dropdown-item ${selectedInterchangeNumber === ic ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedInterchangeNumber(ic);
                            setIcSearch(ic);
                            setShowIcDropdown(false);
                          }}
                        >
                          {ic}
                          {selectedInterchangeNumber === ic && <span className="selected-indicator">✓</span>}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item no-results">No IC Numbers found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>


          <div className="filter_button_blk mt_26" style={{marginLeft: '10px'}}>
             <img 
               src={searchIcon} 
               alt="search" 
               className="" 
               onClick={handleSearch}
               style={{ 
                 cursor: 'pointer',
                 transition: 'transform 0.2s'
               }}
               onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
               onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
             />
           </div>
        </div>
          </div>
      )}


{activeTab === 'data' && (<>
      <div className="d-flex justify-content-between align-items-end mb_15 mt_20">
          <h4 className="font_18 fw-semi mb_0">Finalized Data</h4>
          <div className="d-flex align-items-center gap-4">
          {isSuperAdmin && (
              <button 
               className={`btn d-flex saving_btn align-items-center ${hasUnsavedChanges() ? 'btn-success' : isEditModeEnabled ? 'btn-warning' : 'btn-outline-primary'}`}
                onClick={hasUnsavedChanges() ? handleSaveAllChanges : toggleEditMode}
              disabled={saving}
                style={{
                    border: hasUnsavedChanges() ? 'none' : isEditModeEnabled ? 'none' : '1px solid #ff6600',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: hasUnsavedChanges() ? '#28a745' : isEditModeEnabled ? '#ffc107' : 'transparent',
                color: hasUnsavedChanges() ? '#fff' : isEditModeEnabled ? '#000' : 'black'
              }}      
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : hasUnsavedChanges() ? (
                  <>
                    💾 Save All Changes
                    {hasUnsavedChanges() && (
                      <span className="badge bg-warning text-dark ms-2">
                        {getUnsavedChangesCount()}
                      </span>
                    )}
                  </>
                ):isEditModeEnabled ? (
                <>
                  ✏️ Editable
                </>
              ) : (
                <>
                  ✏️ Edit All Rows
                </>
              )}
              </button>
            )}
            <button 
              className="btn btn-outline-primary d-flex align-items-center"
              onClick={handleDownloadExcel}
              style={{ 
                border: '1px solid #ff6600', 
                borderRadius: '4px', 
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                color: 'black'
              }}
              title="Export to Excel"
            >
              <img 
                src={downloadIcon} 
                alt="Download" 
                style={{ width: '16px', height: '16px', marginRight: '8px' }}
              />
              Export Report
            </button>
          </div>
        </div>

      <div className="table-section p_15 report_table_bg impact_data_blk">
        
        {/* Show placeholder message when no search has been performed */}
        {filteredData.length === 0 && (appliedFilters.regions.length === 0 || appliedFilters.interchangeNumbers.length === 0) && (
          <div style={{ 
            backgroundColor: '#ffffff', 
            borderRadius: '12px', 
            border: '1px solid #dee2e6',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            margin: '20px 0',
            minHeight: '500px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div className="text-center" style={{ maxWidth: '400px' }}>
              <div className="mb-4">
                <i className="fas fa-table text-muted" style={{ fontSize: '64px', opacity: '0.6' }}></i>
              </div>
              <h4 className="text-muted mb-3 fw-bold">Finalized Data</h4>
              <p className="text-muted mb-0 font_16">
                Please select <span className="text-primary fw-bold">Region</span> and <span className="text-primary fw-bold">IC Number</span> filters and click Search to view the finalized data.
              </p>
            </div>
          </div>
        )}
        
        {/* Show message when no data after applying filters */}
        {filteredData.length === 0 && appliedFilters.regions.length > 0 && appliedFilters.interchangeNumbers.length > 0 && (
          <div className="text-center p-5" style={{ 
            backgroundColor: '#f8d7da', 
            borderRadius: '12px', 
            border: '1px solid #f5c6cb',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            margin: '20px 0'
          }}>
            <div className="mb-3">
              <i className="fas fa-info-circle text-danger" style={{ fontSize: '48px' }}></i>
            </div>
            <h4 className="text-danger mb-3 fw-bold">No Data Found</h4>
            <p className="text-dark mb-0 font_16">
              No data matches the selected filters. Please try different combinations of <span className="text-primary fw-bold">Region</span> and <span className="text-primary fw-bold">IC Number</span>.
            </p>
          </div>
        )}
        
        {filteredData.length > 0 && (
          <table className="w-100 report_table impact_analysis_table">
            <thead>
              <tr>
                <th><span style={{display: 'flex', width: 'max-content'}}>Part Name</span></th>
                <th><span style={{display: 'flex', width: 'max-content'}}>Region</span></th>
                <th><span style={{display: 'flex', width: 'max-content'}}>IC Number</span></th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('mileage')}
                  className="sortable-header"
                >
                  <span style={{display: 'flex', width: 'max-content', alignItems: 'center', gap: '5px'}}>
                    Mileage
                    {sortConfig.key === 'mileage' && (
                      <span style={{ fontSize: '12px' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </span>
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('original_price')}
                  className="sortable-header"
                >
                  <span style={{display: 'flex', width: 'max-content', alignItems: 'center', gap: '5px'}}>
                    Original Price
                    {sortConfig.key === 'original_price' && (
                      <span style={{ fontSize: '12px' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </span>
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('adjusted_price')}
                  className="sortable-header"
                >
                  <span style={{display: 'flex', width: 'max-content', alignItems: 'center', gap: '5px'}}>
                    Adjusted Price
                    {sortConfig.key === 'adjusted_price' && (
                      <span style={{ fontSize: '12px' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </span>
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('price_difference')}
                  className="sortable-header"
                >
                  <span style={{display: 'flex', width: 'max-content', alignItems: 'center', gap: '5px'}}>
                    Price Difference
                    {sortConfig.key === 'price_difference' && (
                      <span style={{ fontSize: '12px' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </span>
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('percentage_diff')}
                  className="sortable-header"
                >
                  <span style={{display: 'flex', width: 'max-content', alignItems: 'center', gap: '5px'}}>
                    % difference
                    {sortConfig.key === 'percentage_diff' && (
                      <span style={{ fontSize: '12px' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </span>
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('dollar_positive_perc_limit')}
                  className="sortable-header"
                >
                  <span style={{display: 'flex', width: 'max-content', alignItems: 'center', gap: '5px'}}>
                    $ Positive % Limit
                    {sortConfig.key === 'dollar_positive_perc_limit' && (
                      <span style={{ fontSize: '12px' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </span>
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('dollar_negative_perc_limit')}
                  className="sortable-header"
                >
                  <span style={{display: 'flex', width: 'max-content', alignItems: 'center', gap: '5px'}}>
                    $ Negative % Limit
                    {sortConfig.key === 'dollar_negative_perc_limit' && (
                      <span style={{ fontSize: '12px' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </span>
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('model_predicated_price')}
                  className="sortable-header"
                >
                  <span style={{display: 'flex', width: 'max-content', alignItems: 'center', gap: '5px'}}>
                    Model Predicated Price
                    {sortConfig.key === 'model_predicated_price' && (
                      <span style={{ fontSize: '12px' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </span>
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('created_on')}
                  className="sortable-header"
                >
                  <span style={{display: 'flex', width: 'max-content', alignItems: 'center', gap: '5px'}}>
                    Date&Time
                    {sortConfig.key === 'created_on' && (
                      <span style={{ fontSize: '12px' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </span>
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('approved_by_name')}
                  className="sortable-header"
                >
                  <span style={{display: 'flex', width: 'max-content', alignItems: 'center', gap: '5px'}}>
                    Approved By
                    {sortConfig.key === 'approved_by_name' && (
                      <span style={{ fontSize: '12px' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
               {currentData.map((item, index) => {
               // Calculate percentage difference
               const originalPrice = parseFloat(item.original_price) || 0;
               const adjustedPrice = parseFloat(item.adjusted_price) || 0;
               
               // Check if this item has been modified
               const originalItem = originalData.find(orig => orig.id === item.id);
               const isModified = originalItem && originalItem.adjusted_price !== item.adjusted_price;
               
               // Use API values if not modified, otherwise use real-time calculated values
               const priceDifference = isModified 
                 ? adjustedPrice - originalPrice
                 : (item.price_Difference !== undefined 
                   ? parseFloat(item.price_Difference) 
                   : adjustedPrice - originalPrice);
               
               const percentageDifference = isModified
                 ? (originalPrice > 0 
                   ? (((adjustedPrice - originalPrice) / originalPrice) * 100).toFixed(2)
                   : '0.00')
                 : (item.percentage_Difference !== undefined
                   ? parseFloat(item.percentage_Difference).toFixed(2)
                   : (originalPrice > 0 
                     ? (((adjustedPrice - originalPrice) / originalPrice) * 100).toFixed(2)
                     : '0.00'));
               
               // Determine arrow and color based on percentage difference
               const percentageValue = parseFloat(percentageDifference);
               let arrowIcon = '';
               let arrowColor = '';
               
               if (percentageValue > 0) {
                 arrowIcon = '▲'; // Up arrow for increase
                 arrowColor = '#28a745'; // Green color for arrow
               } else if (percentageValue < 0) {
                 arrowIcon = '▼'; // Down arrow for decrease
                 arrowColor = '#dc3545'; // Red color for arrow
               } else {
                 arrowIcon = '—'; // Minus sign for no change
                 arrowColor = '#6c757d'; // Gray color for arrow
               }
                  const isRowEditing = isEditModeEnabled;
               return (
                 <tr key={item.id || index}>
                   <td>{item.part_name}</td>
                   <td>{item.region}</td>
                   <td>{item.interchange_number}</td>
                   <td>{formatNumber(item.mileage)}</td>
                   <td><span style={{display: 'flex', width: 'max-content'}}>{`${formatNumber(item.original_price)} $`}</span></td>
                   <td>
                     <div className="editable-price-container">
                       <input
                         type="number"
                         className="editable-price-input"
                         defaultValue={item.adjusted_price}
                         readOnly={!isRowEditing}
                         onBlur={(e) => isRowEditing && handlePriceChange(item.id, e.target.value)}
                         onKeyPress={(e) => {
                            if (e.key === 'Enter' && isRowEditing) {
                             e.target.blur(); // Trigger onBlur to save
                           }
                         }}
                         disabled={!isSuperAdmin}
                         style={{
                           width: '80px',
                           padding: '4px 8px',
                            border: isRowEditing ? '1px solid #ddd' : '1px solid #e9ecef',
                           borderRadius: '4px',
                           fontSize: '14px',
                           backgroundColor: isSuperAdmin ? 'white' : '#f8f9fa',
                           cursor: isSuperAdmin ? 'text' : 'not-allowed',
                             backgroundColor: isRowEditing ? '#fff' : '#f8f9fa',
                           cursor: isRowEditing ? 'text' : 'not-allowed',
                           color: isRowEditing ? '#000' : '#6c757d'
                         }}
                       />
                       <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>$</span>
                     </div>
                   </td>
                   <td>
                     <span style={{ 
                       color: priceDifference < 0 ? '#dc3545' : priceDifference > 0 ? '#28a745' : '#6c757d',
                       fontWeight: 'bold'
                     }}>
                       {priceDifference > 0 ? '+' : ''}{formatNumber(priceDifference)} $
                     </span>
                   </td>
                   
                   <td>
                     <span style={{ 
                       color: arrowColor,
                       fontSize: '14px',
                       fontWeight: 'bold',
                       marginRight: '8px'
                     }}>
                       {arrowIcon}
                     </span>
                     <span style={{ color: '#000' }}>
                       {percentageDifference}%
                     </span>
                   </td>
                    <td><span style={{display: 'flex', width: 'max-content'}}>{item.dollar_positive_perc_limit} $</span></td>
                   <td><span style={{display: 'flex', width: 'max-content'}}>{item.dollar_negative_perc_limit} $</span></td>
                   <td><span style={{display: 'flex', width: 'max-content'}}>{item.model_predicated_price} $</span></td>
                   <td><span style={{display: 'flex', width: 'max-content'}}>{formatDateTimeSafe(item.created_on)}</span></td>
                   <td><span style={{display: 'flex', width: 'max-content'}}>{item.approved_by_name}</span></td>
                 </tr>
               );
             })}
            </tbody>
          </table>
        )}
        
        {/* Pagination Controls */}
        {renderPagination()}
      </div>
</>)}

       {/* Unified Chart Section */}
       {activeTab === 'graph' && (
       <div className="sreport_table_bg combined-frame mt_20 p_15" style={{padding: '16px !important', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
         <div className="section-title-bar">
           <h4 className="font_18 fw-semi mb-0">Price Analysis - All Price Types</h4>
         </div>


        {/* {(appliedFilters.regions.length > 0 && appliedFilters.interchangeNumbers.length > 0) && (
          <div className="legend-bar mb-3">
            <span className="legend-title">Legend: Shape & Color = Price Type</span>
            <div className="legend-items">
              <div className="legend-item"><span className="legend-dot dot-green"></span>Revised Price (circle)</div>
              <div className="legend-item"><span className="legend-dot dot-red"></span>Sold Price (x)</div>
                </div>
            <div className="legend-items" style={{ marginTop: 8 }}></div>
          </div>
        )} */}
      
        {/* Unified Chart */}
        <div className="chart-pane"  >
          <div className="chart-container" style={{height: '800px'}}>
            {graphLoading ? (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                minHeight: '700px'
              }}>
                <div className="text-center" style={{ maxWidth: '400px' }}>
                  <div className="bouncing-loader">
                    <div></div><div></div><div></div>
                  </div>
                  <h4 className="text-muted mb-3 fw-bold mt-3">Loading Graph Data...</h4>
                  <p className="text-muted mb-0 font_16">
                    Fetching and filtering data for the chart visualization.
                  </p>
                </div>
              </div>
            ) : appliedFilters.regions.length === 0 || appliedFilters.interchangeNumbers.length === 0 ? (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                minHeight: '700px'
              }}>
                <div className="text-center" style={{ maxWidth: '400px' }}>
                  <div className="mb-4">
                    <i className="fas fa-chart-line text-muted" style={{ fontSize: '64px', opacity: '0.6' }}></i>
                  </div>
                  <h4 className="text-muted mb-3 fw-bold">Price vs Mileage Analysis</h4>
                  <p className="text-muted mb-0 font_16">
                    Please select <span className="text-primary fw-bold">Region</span> and <span className="text-primary fw-bold">IC Number</span> filters and click search to view the price analysis chart.
                  </p>
                </div>
              </div>
            ) : (
              <Plot
                data={prepareUnifiedChartData()}
                layout={prepareUnifiedChartLayout()}
                config={{
                  displayModeBar: true,
                  displaylogo: false,
                  showlegend: true,
                  modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
                  toImageButtonOptions: {
                    format: 'png',
                    filename: 'price_analysis_chart',
                    height: 800,
                    width: 1200,
                    scale: 2
                  }
                }}
                style={{ width: '100%', height: '100%' }}
                useResizeHandler={true}
              />
            )}
          </div>
         </div>
       </div>
       )}

       {/* Inventory Summary Cards - Below Graph */}
       {activeTab === 'graph' && appliedFilters.regions.length > 0 && appliedFilters.interchangeNumbers.length > 0 && (
         <div className="inventory-summary-cards mt-4">
           <div className="section-title-bar mb-3">
             <h4 className="font_18 fw-semi mb-0">Inventory Summary</h4>
           </div>
           {(() => {
             // Get filtered inventory data
             const actualGraphData = graphData && graphData.data ? graphData.data : graphData;
             const inventoryData = actualGraphData && Array.isArray(actualGraphData.inventory_pe) ? actualGraphData.inventory_pe : [];
             
             if (inventoryData.length === 0) {
               return (
                 <div className="no-data-message">
                   <div className="text-center p-4">
                     <i className="fas fa-box-open text-muted" style={{ fontSize: '48px', opacity: '0.6' }}></i>
                     <h5 className="text-muted mt-3 mb-0">No Inventory Data Available</h5>
                   </div>
                 </div>
               );
             }

             // Get the first item for summary (assuming all items in filtered data have similar values)
             const summaryItem = inventoryData[0];
             
             return (
               <div className="summary-cards-grid">
                 {/* Stock Status Card */}
                 <div className="summary-card stock-status-card">
                   <div className="card-icon">
                     <i className="fas fa-check-circle"></i>
                   </div>
                   <div className="card-content">
                     <div className="card-value">{summaryItem.stock_status || 'N/A'}</div>
                     <div className="card-label">Stock Status</div>
                   </div>
                 </div>

                 {/* In Stock (A+B only) Card */}
                 <div className="summary-card in-stock-card">
                   <div className="card-icon">
                     <i className="fas fa-boxes"></i>
                   </div>
                   <div className="card-content">
                     <div className="card-value">{summaryItem.number_of_parts_in_stock_a_b_only || 0}</div>
                     <div className="card-label">In Stock (A+B only)</div>
                   </div>
                 </div>

                 {/* Target QOH (90-day sales) Card */}
                 <div className="summary-card target-qoh-card">
                   <div className="card-icon">
                     <i className="fas fa-bullseye"></i>
                   </div>
                   <div className="card-content">
                     <div className="card-value">{summaryItem.instock_brokered_qty_sold_last_90 || 0}</div>
                     <div className="card-label">Target QOH (90-day sales)</div>
                   </div>
                 </div>

                 {/* Instock Parts Needed Card */}
                 <div className="summary-card parts-needed-card">
                   <div className="card-icon">
                     <i className="fas fa-exclamation-triangle"></i>
                   </div>
                   <div className="card-content">
                     <div className="card-value">{summaryItem.number_of_instock_parts_needed || 0}</div>
                     <div className="card-label">Instock Parts Needed</div>
                   </div>
                 </div>
               </div>
             );
           })()}
         </div>
       )}

       {/* Quartile Configuration Panel - Below Inventory Summary */}
       {activeTab === 'graph' && appliedFilters.regions.length > 0 && appliedFilters.interchangeNumbers.length > 0 && (
         <div className="quartile-config-panel mt-4">
           <div className="section-title-bar mb-3">
             <h4 className="font_18 fw-semi mb-0">Quartile Configuration</h4>
           </div>
           {(() => {
             // Get quartile config data from filtered data
             const actualGraphData = graphData && graphData.data ? graphData.data : graphData;
             const inventoryData = actualGraphData && Array.isArray(actualGraphData.inventory_pe) ? actualGraphData.inventory_pe : [];
             
             // Get global_config from the first item in filtered inventory data
             const quartileConfigs = inventoryData.length > 0 && inventoryData[0].global_config && inventoryData[0].global_config.quartileConfigs 
               ? inventoryData[0].global_config.quartileConfigs 
               : [];
             
             // Get sales data for dynamic quartile calculation
             const sales = actualGraphData && Array.isArray(actualGraphData.sales) ? actualGraphData.sales : [];
             
             // Apply same filters as chart for consistency
             let filteredSales = sales.filter(item => Number(item.mileage) > 0 && item.sold_price !== null && item.sold_price !== undefined);
             
             if (appliedFilters.partTypes.length > 0) {
               filteredSales = filteredSales.filter(item => appliedFilters.partTypes.some(p => item.part_name === p));
             }
             if (appliedFilters.regions.length > 0) {
               filteredSales = filteredSales.filter(item => appliedFilters.regions.some(r => item.region === r));
             }
             if (appliedFilters.interchangeNumbers.length > 0) {
               filteredSales = filteredSales.filter(item => appliedFilters.interchangeNumbers.some(ic => item.interchange_number === ic));
             }
             
             // Calculate dynamic quartile breaks from filtered sales data
             const quartileBreaks = getQuartileBreaks(filteredSales);
             
             // Use dynamic quartile breaks if available, otherwise fall back to hardcoded ranges
             let mileageBuckets;
             if (quartileBreaks && quartileBreaks.length === 5) {
               mileageBuckets = [
                 { mileageRange: `${Math.round(quartileBreaks[0]).toLocaleString()} - ${Math.round(quartileBreaks[1]).toLocaleString()}`, quartileName: "Q1" },
                 { mileageRange: `${Math.round(quartileBreaks[1]).toLocaleString()} - ${Math.round(quartileBreaks[2]).toLocaleString()}`, quartileName: "Q2" },
                 { mileageRange: `${Math.round(quartileBreaks[2]).toLocaleString()} - ${Math.round(quartileBreaks[3]).toLocaleString()}`, quartileName: "Q3" },
                 { mileageRange: `${Math.round(quartileBreaks[3]).toLocaleString()} - ${Math.round(quartileBreaks[4]).toLocaleString()}`, quartileName: "Q4" }
               ];
             } else {
               // Fallback to hardcoded mileage bucket values
               mileageBuckets = [
                 { mileageRange: "20,000 - 50,000", quartileName: "Q1" },
                 { mileageRange: "50,000 - 78,000", quartileName: "Q2" },
                 { mileageRange: "78,000 - 90,000", quartileName: "Q3" },
                 { mileageRange: "90,000 - 135,000", quartileName: "Q4" }
               ];
             }
             
             // Combine API quartile values with dynamic/hardcoded mileage buckets
             const displayQuartiles = mileageBuckets.map((bucket, index) => {
               const apiQuartile = quartileConfigs[index] || { quartileValue: "N/A", quartileName: bucket.quartileName };
               return {
                 quartileName: bucket.quartileName,
                 quartileValue: apiQuartile.quartileValue,
                 mileageRange: bucket.mileageRange
               };
             });
             
             return (
               <div className="quartile-cards-grid">
                 {displayQuartiles.map((quartile, index) => (
                   <div key={index} className={`quartile-card quartile-card-${index + 1}`}>
                     <div className="card-icon">
                       <i className="fas fa-chart-pie"></i>
                     </div>
                     <div className="card-content">
                       <div className="card-value">{quartile.quartileValue}</div>
                       <div className="card-label">{quartile.quartileName}</div>
                       <div className="card-subtitle">{quartile.mileageRange}</div>
                     </div>
                   </div>
                 ))}
               </div>
             );
           })()}
         </div>
       )}

       <style>
        {`
          .mt_20 {
            margin-top: 20px;
          }
          .combined-frame {
            height: auto;
            min-height: auto;
            display: flex;
            flex-direction: column;
            background: #ffffff;
            border-radius: 12px;
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            border: 1px solid #dee2e6;
          }
          .chart-pane {
            flex: 0 0 auto;
            min-height: 800px;
            margin-bottom: 20px;
          }
          .chart-container {
            overflow: visible;
            position: relative;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .report_table_bg.combined-frame {
            padding: 24px;
            overflow: visible;
          }
          .report_table_bg.combined-frame .chart-pane {
            overflow: visible;
          }
          .report_table_bg.combined-frame .chart-container {
            overflow: visible;
          }
          .section-title-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 0;
          }
          .legend-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            background: #fff;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 10px 12px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          }
          .legend-title {
            font-weight: 600;
            color: #495057;
            margin-right: 12px;
            white-space: nowrap;
          }
          .legend-items {
            display: flex;
            flex-wrap: wrap;
            gap: 14px;
          }
          .legend-item {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 6px 10px;
            border: 1px solid #e9ecef;
            border-radius: 999px;
            background: #fafafa;
            font-size: 12px;
            color: #343a40;
          }
          .legend-dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            display: inline-block;
          }
          .legend-diamond {
            width: 10px;
            height: 10px;
            transform: rotate(45deg);
            display: inline-block;
          }
          .legend-x {
            width: 10px;
            height: 10px;
            position: relative;
            display: inline-block;
          }
          .legend-x::before, .legend-x::after {
            content: '';
            position: absolute;
            left: 4px;
            top: 0;
            width: 2px;
            height: 10px;
            background: #d62728;
            border-radius: 1px;
          }
          .legend-x::after {
            transform: rotate(90deg);
          }
          .dot-blue { background: #1f77b4; }
          .dot-orange { background: #ff7f0e; }
          .dot-green { background: #2ca02c; }
          .dot-red { background: #d62728; }
          .font_12 {
            font-size: 12px;
          }
          .font_12.fw-med {
            font-weight: 500;
          }
          .font_14 {
            font-size: 14px;
          }
          .font_16 {
            font-size: 16px;
          }
          .filter_items {
            margin-bottom: 16px;
          }
          .filter_items label {
            margin-bottom: 8px;
            font-weight: 600;
            color: #495057;
          }
          .multiselect-container {
            border-radius: 8px;
            border: 1px solid #ced4da;
            transition: all 0.3s ease;
          }
          .multiselect-container:hover {
            border-color: #007bff;
            box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
          }
          .multiselect-container:focus-within {
            border-color: #007bff;
            box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
          }
          .table-section {
            background: #ffffff;
            border-radius: 12px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            border: 1px solid #dee2e6;
          }
          .impact_analysis_table th {
            background-color: #f8f9fa;
            font-weight: 600;
            color: #495057;
            border-bottom: 2px solid #dee2e6;
            padding: 12px 8px;
          }
          .impact_analysis_table td {
            padding: 12px 8px;
            border-bottom: 1px solid #dee2e6;
            vertical-align: middle;
          }
          .impact_analysis_table tbody tr:hover {
            background-color: #f8f9fa;
          }
          .review-tabs {
            display: flex;
            align-items: center;
            gap: 8px;
            border-bottom: 1px solid #dee2e6;
          }
          .review-tab {
            background: transparent;
            border: none;
            padding: 10px 14px;
            font-weight: 600;
            color: #6c757d;
            border-bottom: 3px solid transparent;
            cursor: pointer;
          }
          .review-tab:hover,
          .review-tab:focus {
            color: #343a40;
            outline: none;
          }
          .review-tab.active {
            color: #ff6600;
            border-bottom-color: #ff6600;
          }
          .tab-badge {
            display: inline-block;
            background-color: #ffefe3;
            color: #ff6600;
            border: 1px solid #ffc299;
            border-radius: 10px;
            font-size: 11px;
            padding: 2px 6px;
            margin-left: 6px;
            vertical-align: middle;
          }
          .impact_data_blk,
          .combined-frame {
            border-radius: 12px;
          }
          /* Searchable dropdown styling (modeled after PriceComparativePage) */
          .searchable-select-container { position: relative; }
          .input-wrapper { position: relative; display: flex; align-items: center; }
          .search-input { width: 100%; border: 1px solid #ced4da; border-radius: 4px; padding: 8px 12px; padding-right: 35px; font-size: 14px; background-color: #fff; transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out; }
          .clear-button { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #6c757d; font-size: 16px; cursor: pointer; padding: 2px 6px; border-radius: 3px; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; width: 20px; height: 20px; }
          .clear-button:hover { background-color: #e9ecef; color: #495057; }
          .search-input:focus { border-color: #80bdff; outline: 0; box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25); }
          .searchable-dropdown { position: absolute; top: 100%; left: 0; right: 0; z-index: 1000; background: white; border: 1px solid #ced4da; border-radius: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); max-height: 200px; overflow-y: auto; }
          .dropdown-list { padding: 0; margin: 0; list-style: none; }
          .dropdown-item { padding: 10px 12px; cursor: pointer; border-bottom: 1px solid #f8f9fa; transition: background-color 0.2s ease; font-size: 14px; display: flex; justify-content: space-between; align-items: center; }
          .dropdown-item:hover { background-color: #f8f9fa; }
          .dropdown-item.selected { background-color: #007bff; color: white; }
          .selected-indicator { font-weight: bold; margin-left: 8px; }
           .dropdown-item.no-results { color: #6c757d; font-style: italic; cursor: default; }
           .dropdown-item.no-results:hover { background-color: transparent; }
           
           /* Quartile Configuration Panel Styling */
           .quartile-config-panel {
             background: #ffffff;
             border-radius: 8px;
             box-shadow: 0 2px 4px rgba(0,0,0,0.1);
             border: 1px solid #dee2e6;
             padding: 20px;
             margin-top: 20px;
           }
           
           .quartile-cards-grid {
             display: grid;
             grid-template-columns: repeat(4, 1fr);
             gap: 16px;
             margin-top: 16px;
           }
           
           .quartile-card {
             background: #ffffff;
             border-radius: 8px;
             padding: 20px;
             box-shadow: 0 1px 3px rgba(0,0,0,0.1);
             border: 1px solid #e9ecef;
             transition: all 0.2s ease;
             display: flex;
             align-items: center;
             gap: 16px;
             min-height: 80px;
             position: relative;
           }
           
           .quartile-card:hover {
             box-shadow: 0 2px 6px rgba(0,0,0,0.15);
             border-color: #dee2e6;
           }
           
           .quartile-card .card-icon {
             font-size: 24px;
             color: #6c757d;
             display: flex;
             align-items: center;
             justify-content: center;
             width: 50px;
             height: 50px;
             border-radius: 8px;
             background: #f8f9fa;
             flex-shrink: 0;
           }
           
           .quartile-card .card-content {
             flex: 1;
             text-align: left;
           }
           
           .quartile-card .card-value {
             font-size: 24px;
             font-weight: 600;
             color: #212529;
             line-height: 1.2;
             margin-bottom: 4px;
           }
           
           .quartile-card .card-label {
             font-size: 14px;
             font-weight: 500;
             color: #6c757d;
             line-height: 1.3;
           }
           
           .quartile-card .card-subtitle {
             font-size: 12px;
             font-weight: 400;
             color: #28a745;
             line-height: 1.2;
             margin-top: 4px;
             font-style: italic;
           }
           
           @media (max-width: 1200px) {
             .quartile-cards-grid {
               grid-template-columns: repeat(2, 1fr);
             }
           }
           
           @media (max-width: 768px) {
             .quartile-cards-grid {
               grid-template-columns: 1fr;
             }
             
             .quartile-card {
               padding: 16px;
               min-height: 70px;
             }
             
             .quartile-card .card-icon {
               width: 40px;
               height: 40px;
               font-size: 20px;
             }
             
             .quartile-card .card-value {
               font-size: 20px;
             }
             
             .quartile-card .card-label {
               font-size: 13px;
             }
           }

           /* Inventory Summary Cards Styling */
           .inventory-summary-cards {
             background: #ffffff;
             border-radius: 8px;
             box-shadow: 0 2px 4px rgba(0,0,0,0.1);
             border: 1px solid #dee2e6;
             padding: 20px;
             margin-top: 20px;
           }
           
           .no-data-message {
             text-align: center;
             padding: 40px;
             background: #f8f9fa;
             border-radius: 8px;
             border: 2px dashed #dee2e6;
           }
           
           .summary-cards-grid {
             display: grid;
             grid-template-columns: repeat(4, 1fr);
             gap: 16px;
             margin-top: 16px;
           }
           
           .summary-card {
             background: #ffffff;
             border-radius: 8px;
             padding: 20px;
             box-shadow: 0 1px 3px rgba(0,0,0,0.1);
             border: 1px solid #e9ecef;
             transition: all 0.2s ease;
             display: flex;
             align-items: center;
             gap: 16px;
             min-height: 80px;
           }
           
           .summary-card:hover {
             box-shadow: 0 2px 6px rgba(0,0,0,0.15);
             border-color: #dee2e6;
           }
           
           .card-icon {
             font-size: 24px;
             color: #6c757d;
             display: flex;
             align-items: center;
             justify-content: center;
             width: 50px;
             height: 50px;
             border-radius: 8px;
             background: #f8f9fa;
             flex-shrink: 0;
           }
           
           .card-content {
             flex: 1;
             text-align: left;
           }
           
           .card-value {
             font-size: 24px;
             font-weight: 600;
             color: #212529;
             line-height: 1.2;
             margin-bottom: 4px;
           }
           
           .card-label {
             font-size: 14px;
             font-weight: 500;
             color: #6c757d;
             line-height: 1.3;
           }
           
           @media (max-width: 1200px) {
             .summary-cards-grid {
               grid-template-columns: repeat(2, 1fr);
             }
           }
           
           @media (max-width: 768px) {
             .summary-cards-grid {
               grid-template-columns: 1fr;
             }
             
             .summary-card {
               padding: 16px;
               min-height: 70px;
             }
             
             .card-icon {
               width: 40px;
               height: 40px;
               font-size: 20px;
             }
             
             .card-value {
               font-size: 20px;
             }
             
             .card-label {
               font-size: 13px;
             }
           }
         `}
      </style>
    </div>
  );
};

export default NewCom;
